import React, { useEffect, useState, useImperativeHandle, useRef } from "react";

/**
 * BoardingDrawer Component
 *
 * Comprehensive pricing system that calculates prices based on:
 * - Basic price from service
 * - Size surcharges (based on pet size)
 * - Room type surcharges (based on selected room type)
 * - Long stay discounts (based on duration)
 * - Multiple boarding discounts (based on number of pets)
 * - Client label discounts (based on client labels)
 *
 * All pricing data comes from the vendorroomservice/getRoomServices API response
 */
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  useMediaQuery,
  useTheme,
  Grid,
  InputAdornment,
  Chip,
  Autocomplete,
} from "@mui/material";
import { FiArrowLeft, FiCalendar, FiEye, FiSend } from "react-icons/fi";
import { MdPets, MdCurrencyRupee, MdLocalHotel } from "react-icons/md";
import PetsIcon from "@mui/icons-material/Pets";
import AddIcon from "@mui/icons-material/Add";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import NotesIcon from "@mui/icons-material/Notes";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
// import AddPetForm from "./AddPetForm";
import PetCard from "./PetCard";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import AddNewPet from "./AddNewPet";
import { useReadOnlyDates } from "../../context/ReadOnlyDatesContext";
import { useOrderBoarding } from "../../context/OrderBoardingContext";
import { createPortal } from "react-dom";
import {useToken } from "../../hooks/token/useToken";

import RoomDrawer from "../../components/Room/RoomDrawer";

import {useProfile} from "../../context/ProfileContext";
import { useAllowPastBooking } from "../../hooks/useAllowPastBooking";

const BoardingDrawer = React.forwardRef(
  (
    {
      isOpen,
      onClose,
      onUpdate,
      clientId,
      itemData,
      orderId,
      fromCalendar = false,
      selectedSlotInfo,
      renderAsContent = false,
      editMode = false,
      onRefreshData = null,
      appointmentStatusRef = null,
      serviceTypeId = null,
      onPetFormStateChange = null,
      fromHistoryReorder = false,
      isPetDetails = false,
      setSaveEnquiryClicked = null,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { allowPastBooking, maxPastDays, minDate, minDateStr } = useAllowPastBooking();

    const [roomTypes, setRoomTypes] = useState([]);
    const [filteredRooms, setFilteredRooms] = useState([]);
    const [roomsDetails, setRoomsDetails] = useState([]);
    const [selectedRoomType, setSelectedRoomType] = useState(null);
    const [pets, setPets] = useState([]);
    const [showNewPetForm, setShowNewPetForm] = useState(false);
    const [isEditPet, setIsEditPet] = useState(false);
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isPetDisabled, setIsPetDisabled] = useState(isPetDetails);
    const pendingBedIdRef = React.useRef(null);
    const [surchargeRooms, setSurchargeRooms] = useState([]);
    const [showServiceNotes, setShowServiceNotes] = useState(false);
    const [showServiceDetailsModal, setShowServiceDetailsModal] =
      useState(false);
    const [showRoomDrawer, setShowRoomDrawer] = useState(false);
    
    const [surchargeRoomsLoaded, setSurchargeRoomsLoaded] = useState(false);

    const [petService, setPetService] = useState({
      pet_id: "",
      checkout_date: "",
      checkin_date: "",
      size: "",
      breed: "",
      pet_type: "",
      room: "",
      appointment_date: "",
      appointment_start_time: "",
      service_id: "",
      roomtype_id: "",
      duration: 0,
      price: 0,
      tax_percentage: 0,
      gst_amount: 0,
      discounted_tax: 0,
      totalAmount: 0,
      notes: "",
      unit_price: 0,
      price_surcharge: [],
      // Add new fields for comprehensive pricing
      basic_price: 0,
      size_surcharge: 0,
      room_type_surcharge: 0,
      long_stay_discount: 0,
      client_label_discount: 0,
      calculated_discounts: {
        long_stay: 0,
        multiple_boarding: 0,
        client_label: 0,
      },
    });
    const [editDataFetched, setEditDataFetched] = useState(false);
    // Price calculation de-dupe controls
    const priceCalcLastParamsRef = React.useRef(null);
    const priceCalcTimerRef = React.useRef(null);
    const priceCalcInFlightRef = React.useRef(false);
    // Refs to carry preselected values when coming from BookingDetailsDrawer
    const preselectedAddonIdsRef = React.useRef(new Set());
    const preselectedRoomTypeIdRef = React.useRef(null);
    const preselectedRoomIdRef = React.useRef(null);
    const [orderItemApplied, setOrderItemApplied] = useState(false);
    const [availableForms, setAvailableForms] = useState([]);
    const [selectedForms, setSelectedForms] = useState([]);
    const [formInputValue, setFormInputValue] = useState("");
    const [loadingForms, setLoadingForms] = useState(false);
    const [sendingForms, setSendingForms] = useState(false);
    const [sentFormNames, setSentFormNames] = useState([]);
    const preselectedFormIdsRef = React.useRef(new Set());
    // Guards to prevent duplicate service fetches and wrong IDs
    const serviceFetchInFlightRef = React.useRef(false);
    const lastFetchedServiceIdRef = React.useRef(null);
    const availableRoomNumbersMapRef = React.useRef({});
        const { isEmployee } = useToken();
    
    const normalizeConsentName = (value = "") =>
      String(value).trim().toLowerCase().replace(/\s+/g, " ");

    const getFormRequirement = (form = {}) => {
      const req = form?.requirement;
      if (req === "mandatory") return "mandatory";
      if (typeof form.requirement === "boolean") {
        return form.requirement ? "mandatory" : "optional";
      }

      // Ignore isRequired (it's for questions, not forms)
      // Default to optional if no requirement field
      return "optional";
    };

    const isFormSent = (form = {}) => {
      const formName = form?.form_name || form?.formName || form?.name || "";
      if (!formName) return false;
      const normalized = normalizeConsentName(formName);
      return sentFormNames.some(
        (sentName) => normalizeConsentName(sentName) === normalized,
      );
    };

    const autoSelectedPetRef = useRef(false);
    
    const currentRoomRef = useRef("");
    currentRoomRef.current = petService.room;

    const [roomAvailabilityMessage, setRoomAvailabilityMessage] = useState("");
    const { hasReadOnlyDates, setHasReadOnlyDates } = useReadOnlyDates();

    const { filterAvailableRooms, isRoomOccupied } = useOrderBoarding();
    const priceCalculatedRef = React.useRef(false);

    // Helper function to safely format date
    const safeFormatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
    };

    // Expose methods to parent component through ref
    // useImperativeHandle(ref, () => ({
    //   closePetForm: () => {
    //     setShowNewPetForm(false);
    //   },
    //   resetFormData: () => {
    //     roomDetailsFetchedRef.current = false;
    //     // In the roomtype_id onChange handler (where you call fetchRoomDetails directly):
    //     roomDetailsFetchedRef.current = true;
    //     resetFormData();
    //   },
    // }));

    useImperativeHandle(ref, () => ({
      closePetForm: () => {
        setShowNewPetForm(false);
      },
      resetFormData: () => {
        roomDetailsFetchedRef.current = false;
        resetFormData();
      },
    }));

    useEffect(() => {
      if (isPetDetails && (itemData?.pet_id || itemData?.vendor_pet_id)) {
        setIsPetDisabled(true);
        if (!petService.pet_id && (itemData.pet_id || itemData.vendor_pet_id)) {
          setPetService((prev) => ({
            ...prev,
            pet_id: itemData.pet_id || itemData.vendor_pet_id,
          }));
          // Fetch pet details immediately
          fetchPetDetails(itemData.pet_id || itemData.vendor_pet_id);
        }
      }
    }, [isPetDetails, itemData, petService.pet_id]);

    useEffect(() => {
      if (
        !isLoading &&
        editDataFetched &&
        roomTypes.length > 0 &&
        petService.roomtype_id
      ) {
        const isValidRoomType = roomTypes.some(
          (room) => room._id === petService.roomtype_id,
        );

        if (!isValidRoomType) {
          console.warn(
            "⚠️ Invalid room type ID detected in validation:",
            petService.roomtype_id,
          );

          // Show toast but DON'T clear the data immediately
          // Let the user see what was originally selected
          toast.warn(
            "The previously selected room type is no longer available. Please select a new room type.",
          );

          // Instead of clearing, mark it as invalid in a way that doesn't break the UI
          // We'll keep the ID but show an error in the dropdown
        }
      }
    }, [roomTypes, petService.roomtype_id, isLoading, editDataFetched]);

    useEffect(() => {
      const init = async () => {
        if (!isOpen || editDataFetched || !itemData?._id) return;

        setIsLoading(true);

        try {
          // 1. First fetch pet details if available
          if (itemData.vendor_pet_id || itemData.pet_id) {
            await fetchPetDetails(itemData.vendor_pet_id || itemData.pet_id);
          }

          // 2. Format dates to YYYY-MM-DD format for input fields
          const formatDateForInput = (dateString) => {
            if (!dateString) return "";
            try {
              const date = new Date(dateString);
              // Handle invalid dates
              if (isNaN(date.getTime())) return "";

              // Convert to YYYY-MM-DD format
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              return `${year}-${month}-${day}`;
            } catch (error) {
              console.error("Error formatting date:", error);
              return "";
            }
          };

          // 3. Set basic form data with properly formatted dates
          const tempFormData = {
            pet_id: itemData.vendor_pet_id || itemData.pet_id || "",
            checkin_date: formatDateForInput(
              itemData.start_date || itemData.checkin_date,
            ),
            checkout_date: formatDateForInput(
              itemData.end_date || itemData.checkout_date,
            ),
            service_id: itemData.service_id || "",
            roomtype_id: itemData.room_type_id || "",
            room: itemData.room_id || "",
            duration: itemData.duration || 0,
            price: itemData.price || 0,
            totalAmount: itemData.total_price || 0,
            notes: itemData.notes || "",
            // Store additional fields if available
            ...(itemData.tax_percentage && {
              tax_percentage: itemData.tax_percentage,
            }),
            ...(itemData.gst_amount && { gst_amount: itemData.gst_amount }),
          };

          setPetService(tempFormData);

          // 4. Calculate duration if we have dates
          if (tempFormData.checkin_date && tempFormData.checkout_date) {
            const checkin = new Date(tempFormData.checkin_date);
            const checkout = new Date(tempFormData.checkout_date);
            const diffInTime = checkout - checkin;
            const diffInDays = Math.ceil(diffInTime / (1000 * 60 * 60 * 24));

            setPetService((prev) => ({
              ...prev,
              duration: diffInDays === 0 ? 1 : diffInDays,
            }));
          }

          // 5. Fetch service details if service_id exists
          if (itemData.service_id) {
            await fetchServiceDetails(itemData.service_id);
          }

          if (itemData.form_ids && Array.isArray(itemData.form_ids)) {
            preselectedFormIdsRef.current = new Set(itemData.form_ids);
          }

          setEditDataFetched(true);
        } catch (error) {
          console.error("Error initializing edit data:", error);
        } finally {
          setIsLoading(false);
        }
      };

      if (!editDataFetched) init();
    }, [isOpen, itemData, editDataFetched]);

    useEffect(() => {
      if (!isOpen || !itemData?._isReschedule) return;

      const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "";
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, "0");
          const d = String(date.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        } catch {
          return "";
        }
      };

      // ✅ Use drop-target room/dates, NOT original booking values
      const checkin = formatDateForInput(itemData.start_date);
      const checkout = formatDateForInput(itemData.end_date);

      setPetService((prev) => ({
        ...prev,
        pet_id: itemData.vendor_pet_id || itemData.pet_id || prev.pet_id,
        checkin_date: checkin,
        checkout_date: checkout,
        service_id: itemData.service_id || prev.service_id,
        roomtype_id: itemData.room_type_id || "", // ← DROP TARGET room type
        room: itemData.room_id || "", // ← DROP TARGET bed
        notes: itemData.notes || "",
        price_calculated_from_api: false,
        last_calculated_params: null,
      }));

      // Store drop-target bed as pending so fetchRoomDetails can apply it
      if (itemData.room_id) {
        pendingBedIdRef.current = itemData.room_id;
      }

      // Calculate duration
      if (checkin && checkout) {
        const diff = Math.ceil(
          (new Date(checkout) - new Date(checkin)) / (1000 * 60 * 60 * 24),
        );
        setPetService((prev) => ({ ...prev, duration: diff === 0 ? 1 : diff }));
      }

      // Fetch pet details so services load
      const petId = itemData.vendor_pet_id || itemData.pet_id;
      if (petId) fetchPetDetails(petId);
      // Mark editDataFetched so the regular edit init block doesn't overwrite
      setEditDataFetched(true);
    }, [isOpen, itemData?._isReschedule]);

    useEffect(() => {
      // Only run when the drawer is open
      if (!isOpen) return;

      // Only auto-select once per session
      if (autoSelectedPetRef.current) return;

      // Don't override an already selected pet
      if (petService.pet_id) return;

      // Wait until the pets list is available
      if (!pets || pets.length === 0) return;

      // Consider only active pets (matching the dropdown filter)
      const activePets = pets.filter(
        (pet) => pet.status?.toLowerCase() !== "inactive",
      );

      if (activePets.length === 1) {
        const singlePet = activePets[0];
        setPetService((prev) => ({ ...prev, pet_id: singlePet._id }));
        fetchPetDetails(singlePet._id);
        autoSelectedPetRef.current = true; // prevent future auto‑selection
      }
    }, [isOpen, pets, petService.pet_id]);

    // Add this to handle date formatting for the non-edit mode as well
    useEffect(() => {
      if (!isOpen || itemData?._id || petDetails?._id) return;

      const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "";

          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        } catch (error) {
          console.error("Error formatting date:", error);
          return "";
        }
      };

      const startDate = itemData?.start_date || selectedSlotInfo?.startDate;
      const endDate = itemData?.end_date || selectedSlotInfo?.endDate;

      // Get bed ID from itemData (from calendar drag)
      const bedId = itemData?.bedId || itemData?.roomId || itemData?.room_id;
      const roomTypeId = itemData?.room_type_id;

      const tempFormData = {
        pet_id: itemData?.pet_id || itemData?.vendor_pet_id || "",
        checkin_date: formatDateForInput(startDate) || "",
        checkout_date: formatDateForInput(endDate) || "",
        service_id: itemData?.service_id || "",
        roomtype_id: roomTypeId || "", // Keep room type ID if provided
        // DON'T set room here yet - wait until rooms are loaded
        // room: bedId || "", // Remove this line
        duration: 0,
        price: 0,
        basic_price: 0,
        tax_percentage: 0,
        gst_amount: 0,
        totalAmount: 0,
        notes: itemData?.notes || "",
      };

      // CRITICAL: Set the form data immediately but NOT the room
      setPetService(tempFormData);

      // Store bedId separately to set later when rooms are loaded
      const bedIdToSet = bedId;

      // If we have a bedId from calendar drag, store it to set later
      if (bedIdToSet) {
        // Store it in a ref or state to use later
        pendingBedIdRef.current = bedIdToSet;
      }

      // If this is from calendar drag, mark that we should auto-fetch services
      if (itemData?.fromDrag) {
        setShouldAutoFetchServices(true);
      }

      // Calculate duration
      if (tempFormData.checkin_date && tempFormData.checkout_date) {
        const checkin = new Date(tempFormData.checkin_date);
        const checkout = new Date(tempFormData.checkout_date);
        const diffInTime = checkout - checkin;
        const diffInDays = Math.ceil(diffInTime / (1000 * 60 * 60 * 24));

        setPetService((prev) => ({
          ...prev,
          duration: diffInDays === 0 ? 1 : diffInDays,
        }));
      } else {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        const formatYMD = (date) => {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, "0");
          const d = String(date.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        };

        const checkin = new Date();
        const checkout = new Date(tomorrow);
        const diffInTime = checkout - checkin;
        const diffInDays = Math.ceil(diffInTime / (1000 * 60 * 60 * 24));

        setPetService((prev) => ({
          ...prev,
          duration: diffInDays === 0 ? 1 : diffInDays,
        }));

        setPetService((prev) => ({
          ...prev,
          checkin_date: formatYMD(today),
          checkout_date: formatYMD(tomorrow),
        }));
      }
    }, [isOpen, itemData, selectedSlotInfo, editDataFetched]);

    //new
    const roomDetailsFetchedRef = React.useRef(false);

    // ✅ NEW: stable key that changes whenever the selected room type's
    // available_room_numbers change (e.g. dates changed → API returned new list)
    const selectedRoomAvailabilityKey = React.useMemo(() => {
      if (!petService.roomtype_id || !roomTypes?.length) return "";
      const rt = roomTypes.find((r) => r._id === petService.roomtype_id);
      if (!rt) return "";
      return (rt.available_room_numbers || [])
        .map((r) => r._id)
        .sort()
        .join(",");
    }, [petService.roomtype_id, roomTypes]);

    // ✅ FIX: re-run fetchRoomDetails whenever the availability of the
    // currently selected room type changes (e.g. dates changed). Previously
    // this was guarded by roomDetailsFetchedRef so it only ran once, leaving
    // the Room Number dropdown showing stale beds after a date change.
    useEffect(() => {
      if (!petService.roomtype_id) return;
      if (!roomTypes || roomTypes.length === 0) return;
      if (!roomTypes.some((r) => r._id === petService.roomtype_id)) return;

      // Keep the ref in sync so resetFormData / useImperativeHandle still work
      roomDetailsFetchedRef.current = true;

      fetchRoomDetails(petService.roomtype_id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petService.roomtype_id, selectedRoomAvailabilityKey]);

    // Add a separate effect to handle room details fetching when both roomTypes and roomtype_id are ready
    // useEffect(() => {
    //   if (petService.roomtype_id && roomTypes.length > 0 && !editDataFetched) {
    //     fetchRoomDetails(petService.roomtype_id);
    //   }
    // }, [petService.roomtype_id, roomTypes.length, editDataFetched]);

    const [petDetails, setPetDetails] = useState("");
    const [petDetailsLoading, setPetDetailsLoading] = useState(false);
    const [rooms, setRooms] = useState([]);
    const [servicesd, setServicesd] = useState({});
    const [serviceDetailData, setServiceDetailData] = useState(null);
    const [shouldAutoFetchServices, setShouldAutoFetchServices] =
      useState(false);
    const [serviceRefreshKey, setServiceRefreshKey] = useState(0);

    const fetchVendorClientPets = async () => {
      setIsLoading(true);
      try {
        const [petsRes, typesRes, breedsRes] = await Promise.all([
          httpService.get(`/vendorpet/getvendorclientpets/${clientId}`),
        ]);
        setPets(petsRes.data);
      } catch (error) {
        console.error("Error loading pet types or breeds", error);
      } finally {
        setIsLoading(false);
      }
    };
    useEffect(() => {
      if (isOpen) {
        fetchVendorClientPets();
      }
    }, [isOpen, clientId, serviceTypeId]);

    const fetchForms = async () => {
      if (!clientId || !petService.pet_id) {
        setAvailableForms([]);
        setSelectedForms([]);
        return;
      }

      setLoadingForms(true);
      try {
        // Priority 1: use service category ID from selected service
        const categoryId = serviceDetailData?.service_category?._id;
        // Priority 2: use serviceTypeId prop
        // Priority 3: fallback to derived key (e.g., "boarding")
        const getServiceCategoryKey = (serviceTypeName = "") => {
          const normalized = String(serviceTypeName).trim().toLowerCase();
          if (normalized.includes("boarding") || normalized.includes("resort"))
            return "boarding";
          if (normalized.includes("grooming")) return "grooming";
          if (normalized.includes("vet") || normalized.includes("pet_vet"))
            return "pet_vet";
          if (normalized.includes("day care") || normalized.includes("daycare"))
            return "day_care";
          if (normalized.includes("training")) return "training";
          if (normalized.includes("shop")) return "pet_shop";
          return normalized.replace(/\s+/g, "_");
        };
        const serviceIdentifier =
          categoryId ||
          serviceTypeId ||
          getServiceCategoryKey(petService.service_type_name || "");

        const params = new URLSearchParams({
          clientId,
          petId: petService.pet_id,
          serviceName: serviceIdentifier, // now sends ID like grooming drawer
        });

        const res = await httpService.get(
          `/consentForm/getavailableFormsForBooking?${params.toString()}`,
        );

        const formsArray = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];

        const normalizedForms = formsArray.map((f) => ({
          ...f,
          _id: f.form_id,
          form_name: f.form_name,
        }));

        // Optional client-side filter (same as grooming)
        const serviceCategoryKey = getServiceCategoryKey(
          petService.service_type_name || "",
        );
        const filteredForms = normalizedForms.filter((f) => {
          if (!Array.isArray(f.services) || f.services.length === 0)
            return true;
          return f.services.some((s) => {
            const str = String(s).trim();
            return (
              str === String(serviceIdentifier) ||
              str.toLowerCase() === serviceCategoryKey
            );
          });
        });

        setAvailableForms(filteredForms);

        // Mandatory forms
        const mandatoryForms = filteredForms.filter(
          (f) => getFormRequirement(f) === "mandatory",
        );
        const mandatoryIds = new Set(mandatoryForms.map((f) => f._id));

        // Preselected from order (edit mode)
        const preselectedIds = new Set([
          ...(itemData?.form_ids && Array.isArray(itemData.form_ids)
            ? itemData.form_ids
            : []),
          ...Array.from(preselectedFormIdsRef.current || new Set()),
        ]);

        const allSelectedIds = new Set([...mandatoryIds, ...preselectedIds]);
        const selected = filteredForms.filter((f) => allSelectedIds.has(f._id));

        setSelectedForms(selected);
      } catch (error) {
        console.error("❌ Error fetching booking forms:", error);
        setAvailableForms([]);
        setSelectedForms([]);
      } finally {
        setLoadingForms(false);
      }
    };

    useEffect(() => {
      if (isOpen && clientId && petService.pet_id) {
        fetchForms();
      } else {
        setAvailableForms([]);
        setSelectedForms([]);
      }
    }, [isOpen, clientId, petService.pet_id, serviceDetailData, serviceTypeId]);
    const fetchSentForms = async () => {
      const resolvedClientId =
        clientId ||
        itemData?.customer_id ||
        itemData?.vendor_client_id ||
        itemData?.client_id;

      if (!resolvedClientId) return;

      try {
        const res = await httpService.get(
          `/vendorclient/getformdetails/${resolvedClientId}`,
        );

        // Handle both possible response structures:
        // - res.data is the array directly
        // - res.data.data is the array
        const forms = res.data?.data ?? res.data ?? [];

        // Extract form names where status === "sent"
        const sentNames = forms
          .filter((form) => form.status?.toLowerCase() === "sent")
          .map((form) => form.form_name)
          .filter(Boolean);

        setSentFormNames(sentNames);
      } catch (error) {
        console.error("Error fetching client form details:", error);
      }
    };

    const getConsentServiceType = (categoryName = "") => {
      const name = String(categoryName).toLowerCase();
      if (name.includes("grooming")) return "grooming";
      if (
        name.includes("boarding") ||
        name.includes("pet resort") ||
        name.includes("daycare")
      )
        return "boarding";
      if (name.includes("vet") || name.includes("clinic")) return "pet_vet";
      return "boarding";
    };

    const getConsentServiceRefId = () =>
      itemData?._id ||
      itemData?.booking_id ||
      itemData?.order_id ||
      orderId ||
      itemData?.id ||
      itemData?.ref_id;

    const handleSendSelectedForms = async () => {
      if (!selectedForms || selectedForms.length === 0) {
        toast.warning("Please select at least one form to send.");
        return;
      }

      if (!petService?.pet_id) {
        toast.warning("Please select a pet before sending the form.");
        return;
      }

      const resolvedClientId =
        clientId ||
        itemData?.customer_id ||
        itemData?.vendor_client_id ||
        itemData?.client_id;

      if (!resolvedClientId) {
        toast.error("Client ID not found. Cannot send form.");
        return;
      }

      // Filter out already sent forms
      const formsToSend = selectedForms.filter((form) => !isFormSent(form));
      if (formsToSend.length === 0) {
        toast.info("All selected forms have already been sent.");
        return;
      }

      setSendingForms(true);
      try {
        // Call the POST endpoint with only the status
        await httpService.post(
          `/vendorclient/upadteformstatus/${resolvedClientId}`,
          { status: "sent" },
        );

        // Refresh the sent list
        await fetchSentForms();

        toast.success(`${formsToSend.length} form(s) sent successfully!`);
      } catch (error) {
        console.error("Failed to send forms:", error);
        toast.error("Failed to send forms. Please try again.");
      } finally {
        setSendingForms(false);
      }
    };
    useEffect(() => {
      if (isOpen && clientId && petService.pet_id) {
        fetchForms();
      } else {
        setAvailableForms([]);
        setSelectedForms([]);
      }
    }, [isOpen, clientId, petService.pet_id, serviceDetailData, serviceTypeId]);

    

    useEffect(() => {
      if (!isOpen) {
        setAvailableForms([]);
        setSelectedForms([]);
        preselectedFormIdsRef.current = new Set();
        autoSelectedPetRef.current = false;
      }
    }, [isOpen]);

    // Add this useEffect to handle setting bedId when rooms are available
    useEffect(() => {
      // Only run if we have a pending bedId and rooms are loaded
      if (pendingBedIdRef.current && rooms.length > 0) {
        const bedId = pendingBedIdRef.current;

        // Check if the bedId exists in the loaded rooms
        const roomExists = rooms.some((room) => room._id === bedId);

        if (roomExists) {
          setPetService((prev) => ({
            ...prev,
            room: bedId,
          }));
        } else {
          console.warn(
            "⚠️ Bed ID from calendar drag not found in available rooms:",
            bedId,
          );
          // Optionally show a toast
          toast.warn(
            "The selected room/bed is no longer available. Please select a different room.",
          );
        }

        // Clear the pending bedId
        pendingBedIdRef.current = null;
      }
    }, [rooms]); // Run whenever rooms change

    useEffect(() => {
      const fetchRoomServices = async () => {
        // First, determine the pet type
        let petType = "";

        // Priority 1: From selected pet details (if pet is selected)
        if (petDetails?.pet_type?.name) {
          petType = petDetails.pet_type.name.toLowerCase().trim();
        }
        // Priority 2: From pet service state (if pet type was already determined somehow)
        else if (petService.pet_type) {
          petType = petService.pet_type.toLowerCase().trim();
        }
        // Priority 3: Try to get from room type restrictions if no pet selected yet
        else if (petService.roomtype_id && roomTypes.length > 0) {
          const selectedRoomType = roomTypes.find(
            (room) => room._id === petService.roomtype_id,
          );
          if (selectedRoomType?.pet_type?.length > 0) {
            petType = selectedRoomType.pet_type[0]?.name?.toLowerCase().trim();
          }
        }

        if (!petType) {
          return;
        }

        try {
          const petSize =
            petDetails?.size?.size_label?.toLowerCase() ||
            petService.pet_size?.toLowerCase() ||
            null;

          const roomTypeId = petService.roomtype_id || null;

          // let apiUrl = `/vendorroomservice/getRoomServices?serviceCategoryTypeId=${
          //   serviceTypeId || null
          // }&status=active&petType=${petType}`;

          const requestBody = {
            status: "active",
            petType,
            ...(petSize && { size: petSize }),
            ...(roomTypeId && { roomTypeId }),
            ...(petService.checkin_date && {
              checkinDate: petService.checkin_date,
            }),
            ...(petService.checkout_date && {
              checkoutDate: petService.checkout_date,
            }),
          };

          const response = await httpService.post(
            `/vendorroomservice/getRoomServices`,
            requestBody,
          );

          const filteredServices = response.data.filter((service) => {
            if (!Array.isArray(service.pet_type)) {
              return false;
            }
            const servicePetTypeNames = service.pet_type
              .map((pt) => pt?.name?.toLowerCase()?.trim())
              .filter((name) => name);
            return servicePetTypeNames.includes(petType);
          });

          setServices(filteredServices);

          // ✅ If service is already selected, update its price with new date-based pricing
          if (petService.service_id && filteredServices.length > 0) {
            const currentService = filteredServices.find(
              (s) => s._id === petService.service_id,
            );
            if (currentService && !priceCalculatedRef.current) {
              setPetService((prev) => ({
                ...prev,
                mrp_price: currentService.mrp_price,
                sale_price: currentService.sale_price,
                discount_percentage: currentService.discount_percentage,
                size_surcharge:
                  prev.size_surcharge > 0
                    ? prev.size_surcharge
                    : currentService.calculated_surcharges?.size_surcharge || 0,
                room_type_surcharge:
                  prev.room_type_surcharge > 0
                    ? prev.room_type_surcharge
                    : currentService.calculated_surcharges
                        ?.room_type_surcharge || 0,
                basic_price: currentService.basic_price,
                // ✅ Force price recalculation
                price_calculated_from_api: false,
                last_calculated_params: null,
              }));

              // ✅ Trigger price recalculation with new dates
              setTimeout(() => {
                if (
                  petService.pet_id &&
                  petService.service_id &&
                  petService.roomtype_id &&
                  petService.checkin_date &&
                  petService.checkout_date
                ) {
                  calculatePriceFromAPI();
                }
              }, 300);
            }
          }

          if (!filteredServices.length) {
            toast.warn(
              `Service not added for the pet type "${petType}". Please add Service for pet Type ${petType} and try again!!`,
            );
          }

          if (
            itemData?.fromDrag &&
            !petService.service_id &&
            filteredServices.length > 0
          ) {
            const firstService = filteredServices[0];
            setPetService((prev) => ({
              ...prev,
              service_id: firstService._id,
              mrp_price: firstService.mrp_price,
              sale_price: firstService.sale_price,
              discount_percentage: firstService.discount_percentage,
              size_surcharge:
                firstService.calculated_surcharges?.size_surcharge || 0,
              room_type_surcharge:
                firstService.calculated_surcharges?.room_type_surcharge || 0,
            }));
            fetchServiceDetails(firstService._id);
          }
        } catch (error) {
          console.error("Error fetching services:", error);
        }
      };

      // Fetch services when:
      // 1. Pet details are loaded (pet selected)
      // 2. OR when room type is selected/changed (for calendar drag scenario)
      // 3. OR when pet type is manually set in petService
      // 4. OR when pet size changes
      if (petDetails?._id || petService.roomtype_id || petService.pet_type) {
        fetchRoomServices();
      }
    }, [
      petDetails?._id,
      petDetails?.size?.size_label, // ✅ Re-fetch when pet size changes
      petService.roomtype_id, // ✅ Re-fetch when room type changes
      petService.pet_type,
      petService.checkin_date,
      petService.checkout_date,
      serviceTypeId,
      itemData?.fromDrag,
      petService.service_id, // ✅ Add this to re-fetch when service changes (optional)
      serviceRefreshKey,
    ]);

    // Also, add this effect to update pet type when pet details change
    useEffect(() => {
      if (petDetails?.pet_type?.name) {
        setPetService((prev) => ({
          ...prev,
          pet_type: petDetails.pet_type.name,
        }));
      }
    }, [petDetails?.pet_type?.name]);

    // Ensure pet selection is set once pets are loaded (especially when editing from calendar/create appointment)
    useEffect(() => {
      if (!isOpen && !isLoading) return;
      if (!pets || pets.length === 0) return;

      const candidateId = itemData?.pet_id || itemData?.vendor_pet_id;
      if (candidateId) {
        const exists = pets.some((p) => p._id === candidateId);
        if (exists) {
          setPetService((prev) => ({ ...prev, pet_id: candidateId }));
          // IMPORTANT: fetch pet details now so UI has pet_name immediately
          fetchPetDetails(candidateId);
          return;
        }
      }
      // Fallback: try by pet_name if id mismatch
      else if (itemData?.pet_name) {
        const match = pets.find(
          (p) =>
            (p.name || "").toLowerCase() ===
            (itemData.pet_name || "").toLowerCase(),
        );
        if (match?._id) {
          setPetService((prev) => ({ ...prev, pet_id: match._id }));
          fetchPetDetails(match._id);
        }
      }
    }, [
      isOpen,
      pets,
      itemData?.pet_id,
      itemData?.vendor_pet_id,
      itemData?.pet_name,
    ]);

    useEffect(() => {
      if (petService.checkin_date && petService.checkout_date) {
        const checkin = new Date(petService.checkin_date);
        const checkout = new Date(petService.checkout_date);

        // Validate dates
        if (isNaN(checkin.getTime()) || isNaN(checkout.getTime())) {
          setPetService((prev) => ({
            ...prev,
            duration: 0,
          }));
          return;
        }

        const diffInTime = checkout - checkin;
        const diffInDays = Math.ceil(diffInTime / (1000 * 60 * 60 * 24));

        setPetService((prev) => ({
          ...prev,
          duration: diffInDays === 0 ? 1 : diffInDays,
        }));
      } else {
        // Clear duration if dates are not set
        setPetService((prev) => ({
          ...prev,
          duration: 0,
        }));
      }
    }, [
      petService.checkin_date,
      petService.checkout_date,
      petService.service_details,
    ]);

    useEffect(() => {
      const checkAvailabilityAndFilter = async () => {
        // Skip if no surcharge rooms from service
        if (surchargeRooms.length === 0) {
          setFilteredRooms([]);
          setRoomAvailabilityMessage("Please select a service first");
          return;
        }

        // Skip if required fields are missing
        if (
          !petService.checkin_date ||
          !petService.checkout_date ||
          !petService.pet_id
        ) {
          return;
        }

        // Skip if pet details not loaded
        if (!petDetails || !petDetails.pet_type) {
          setRoomAvailabilityMessage("Loading pet details...");
          return;
        }

        const payload = {
          checkIn: petService.checkin_date,
          checkOut: petService.checkout_date,
          type: "boarding",
          excludingBoardingId: itemData?.ref_id || null,
          petType: petDetails.pet_type?.name || "",
        };

        try {
          setRoomAvailabilityMessage("Checking room availability...");
          const response = await httpService.post(
            `/room/getAvailableVendorRooms`,
            payload,
          );
          let availableRooms = response.data;

          // Filter out rooms already occupied by other items in this order
          if (
            filterAvailableRooms &&
            petService.checkin_date &&
            petService.checkout_date &&
            !itemData?._id
          ) {
            availableRooms = filterAvailableRooms(
              availableRooms,
              petService.checkin_date,
              petService.checkout_date,
              itemData?._editIndex, // exclude current item when editing
            );
          }

          const availableRoomIds = new Set(
            response.data.map((room) => room._id),
          );

          // Filter surcharge rooms to only those that are available on selected dates
          const availableSurchargeRooms = surchargeRooms.filter((room) =>
            availableRoomIds.has(room._id),
          );

          setFilteredRooms(availableSurchargeRooms);
          setRoomTypes(availableRooms); // Store for reference
          const stillHasSelectedType = availableSurchargeRooms.some(
            (r) => r._id === petService.roomtype_id,
          );

          if (petService.roomtype_id && !stillHasSelectedType) {
            setSelectedRoomType(null);
            setRooms([]);
            setRoomsDetails([]);
            priceCalculatedRef.current = false;
            setPetService((prev) => ({
              ...prev,
              roomtype_id: "",
              room: "",
              room_type_surcharge: 0,
              price_calculated_from_api: false,
              last_calculated_params: null,
            }));
          }

          if (availableSurchargeRooms.length === 0) {
            setRoomAvailabilityMessage(
              `No rooms with surcharges available for ${petDetails.pet_type?.name || "selected"} pets on these dates`,
            );
          } else {
            setRoomAvailabilityMessage("");
          }
        } catch (error) {
          console.error("Error checking room availability:", error);
          setRoomAvailabilityMessage("Error loading available rooms");
          toast.error("Failed to fetch available rooms");
        }
      };

      checkAvailabilityAndFilter();
    }, [
      petService.checkin_date,
      petService.checkout_date,
      petService.pet_id,
      petDetails?._id,
      itemData?._id,
      surchargeRooms, // Re-run when surcharge rooms change (when service selected)
      filterAvailableRooms,
      itemData?._editIndex,
    ]);

    const handlePetServiceChange = (field, value) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const formatDateForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      // Validation for check-in date
      if (field === "checkin_date") {
        if (!value) {
          setPetService((prev) => ({
            ...prev,
            checkin_date: value,
            ...(field === "checkin_date" || field === "checkout_date"
              ? {
                  price_calculated_from_api: false,
                  last_calculated_params: null,
                }
              : {}),
          }));
          return;
        }

        const checkinDate = new Date(value);
        checkinDate.setHours(0, 0, 0, 0);

        // if (checkinDate < today) {
        //   toast.error("Check-in date cannot be in the past.");
        //   return;
        // }
        if (checkinDate < minDate) {
          toast.error(
            allowPastBooking
              ? `Check-in can be at most ${maxPastDays} day(s) in the past.`
              : "Check-in date cannot be in the past.",
          );
          return;
        }

        if (petService.checkout_date) {
          const checkoutDate = new Date(petService.checkout_date);
          checkoutDate.setHours(0, 0, 0, 0);

          if (checkinDate >= checkoutDate) {
            const adjustedCheckout = new Date(checkinDate);
            adjustedCheckout.setDate(adjustedCheckout.getDate() + 1);

            toast.info(
              "Check-in date is after checkout date. Checkout has been adjusted to the next day.",
            );

            setPetService((prev) => ({
              ...prev,
              checkin_date: value,
              checkout_date: formatDateForInput(adjustedCheckout),
              price_calculated_from_api: false,
              last_calculated_params: null,
            }));
            return;
          }
        }
      }

      // Validation for check-out date
      if (field === "checkout_date") {
        if (!value) {
          setPetService((prev) => ({
            ...prev,
            checkout_date: value,
            ...(field === "checkin_date" || field === "checkout_date"
              ? {
                  price_calculated_from_api: false,
                  last_calculated_params: null,
                }
              : {}),
          }));
          return;
        }

        const checkinDate = petService.checkin_date
          ? new Date(petService.checkin_date)
          : null;
        const checkoutDate = new Date(value);
        checkoutDate.setHours(0, 0, 0, 0);

        if (checkinDate) {
          checkinDate.setHours(0, 0, 0, 0);

          if (checkoutDate <= checkinDate) {
            const adjustedCheckout = new Date(checkinDate);
            adjustedCheckout.setDate(adjustedCheckout.getDate() + 1);

            toast.info(
              "Checkout date was adjusted to the next day after check-in.",
            );

            setPetService((prev) => ({
              ...prev,
              checkout_date: formatDateForInput(adjustedCheckout),
              price_calculated_from_api: false,
              last_calculated_params: null,
            }));
            return;
          }
        }
      }

      setPetService((prev) => ({
        ...prev,
        [field]: value,
        // ✅ Reset price calculation flags when dates change
        ...(field === "checkin_date" || field === "checkout_date"
          ? {
              price_calculated_from_api: false,
              last_calculated_params: null,
            }
          : {}),
      }));

      // If pet is selected, fetch pet details immediately to get pet type
      if (field === "pet_id" && value) {
        fetchPetDetails(value);
      }
    };

    // Effect to notify parent about pet form state changes
    useEffect(() => {
      if (onPetFormStateChange && renderAsContent) {
        onPetFormStateChange(showNewPetForm);
      }
    }, [showNewPetForm, onPetFormStateChange, renderAsContent]);

    // Replace your existing fetchRoomDetails function with this version:
    const fetchRoomDetails = async (
      roomtype_id,
      roomNumbersMapOverride = null,
      silent = false,
    ) => {
      try {
        // Enhanced guard clause with better logging
        if (!roomTypes || roomTypes.length === 0) {
          console.warn(
            "🚫 fetchRoomDetails - Room types not loaded yet, roomTypes:",
            roomTypes?.length || 0,
          );
          return null;
        }

        const selectedRoomType = roomTypes.find(
          (room) => room._id === roomtype_id,
        );

        if (!selectedRoomType) {
          console.warn(
            "⚠️ fetchRoomDetails - Room type not found in available types:",
            roomtype_id,
          );
          console.warn(
            "Available room types:",
            roomTypes.map((r) => ({ id: r._id, name: r.room_name || r.name })),
          );
          return null;
        }
        // new
        // ✅ FIX: Use available_room_numbers from API response directly
        // The API already filters out occupied beds and returns only available ones
        const availableRoomNumbers =
          selectedRoomType.available_room_numbers || [];

        // Fallback: if available_room_numbers is empty, filter room_numbers manually
        let filteredRoomNumbers;
        if (availableRoomNumbers.length > 0) {
          // Build full room objects from room_numbers using the available IDs
          const availableIds = new Set(availableRoomNumbers.map((r) => r._id));
          filteredRoomNumbers = (selectedRoomType.room_numbers || []).filter(
            (room) => availableIds.has(room._id) && room.is_available !== false,
          );
        } else {
          // Fallback to manual filtering
          filteredRoomNumbers = (selectedRoomType.room_numbers || []).filter(
            (room) => room.is_available !== false,
          );
        }

        // Additionally apply the roomNumbersMapOverride if provided
        if (roomNumbersMapOverride && roomNumbersMapOverride[roomtype_id]) {
          const mapBedIds = roomNumbersMapOverride[roomtype_id];
          if (mapBedIds.size > 0) {
            filteredRoomNumbers = filteredRoomNumbers.filter((room) =>
              mapBedIds.has(room._id),
            );
          }
        } else if (availableRoomNumbersMapRef.current[roomtype_id]) {
          // Use ref if no override
          const refBedIds = availableRoomNumbersMapRef.current[roomtype_id];
          if (refBedIds.size > 0) {
            filteredRoomNumbers = filteredRoomNumbers.filter((room) =>
              refBedIds.has(room._id),
            );
          }
        }

        // Exclude rooms occupied by other items in the same order
        if (
          isRoomOccupied &&
          petService.checkin_date &&
          petService.checkout_date
        ) {
          filteredRoomNumbers = filteredRoomNumbers.filter(
            (room) =>
              !isRoomOccupied(
                room._id,
                petService.checkin_date,
                petService.checkout_date,
                itemData?._editIndex,
              ),
          );
        }

        if (filteredRoomNumbers.length === 0) {
          toast.warn(
            "No rooms available for the selected dates in this room type.",
          );
        }

        setRooms(filteredRoomNumbers);
        setRoomsDetails(filteredRoomNumbers);

        // ✅ NEW: if the currently selected room number is no longer available,
        // clear it so the dropdown reflects the fresh availability
        const currentRoom = currentRoomRef.current;
          if (
            !silent &&
            currentRoom &&
            !filteredRoomNumbers.some((r) => r._id === currentRoom)
          ) {
            toast.warn("Selected room is not available. Please choose another room.", {
              toastId: "boarding-room-unavailable",
            });
          }

        setPetService((prev) => {
          if (!prev.room) return prev;
          const stillAvailable = filteredRoomNumbers.some(
            (r) => r._id === prev.room,
          );
         //return prev;
          return stillAvailable ? prev : { ...prev, room: "" };
        });

        // Apply pending bed from reschedule/calendar drag
        const targetBedId =
          pendingBedIdRef.current ||
          (itemData?._isReschedule ? itemData?.room_id : null);
        if (targetBedId) {
          const exists = filteredRoomNumbers.some((r) => r._id === targetBedId);
          if (exists) {
            setPetService((prev) => ({ ...prev, room: targetBedId }));
          } else {
            toast.warn(
              "The pre-selected room is not available for these dates.",
            );
            setPetService((prev) => ({ ...prev, room: "" }));
          }
          if (pendingBedIdRef.current) pendingBedIdRef.current = null;
        }

        // Handle preselected room in regular edit mode
        if (
          editMode &&
          !itemData?._isReschedule &&
          preselectedRoomIdRef.current &&
          !petService.room
        ) {
          const exists = filteredRoomNumbers.some(
            (r) => r._id === preselectedRoomIdRef.current,
          );
          if (exists) {
            setPetService((prev) => ({
              ...prev,
              room: preselectedRoomIdRef.current,
            }));
          }
        }
        return selectedRoomType;
      } catch (error) {
        console.error(
          "💥 fetchRoomDetails - Failed to fetch room details:",
          error,
        );
        return null;
      }
    };

    const fetchPetDetails = async (petId) => {
      setPetDetailsLoading(true);
      try {
        const response = await httpService.get(
          `/vendorpet/getvendorpet/${petId}`,
        );
        const petData = response.data;
        setPetDetails(petData);

        // Update pet type in petService state
        if (petData.pet_type?.name) {
          setPetService((prev) => ({
            ...prev,
            pet_type: petData.pet_type.name,
          }));
        }
        return petData;
      } catch (error) {
        console.error("Failed to fetch pet details:", error);
        return null;
      } finally {
        setPetDetailsLoading(false);
      }
    };

    // Debounced single-shot scheduler to avoid multiple API calls for the same params
    const schedulePriceCalculation = React.useCallback(() => {
      if (
        !clientId ||
        !petService.pet_id ||
        !petService.service_id ||
        !petService.roomtype_id ||
        !petService.checkin_date ||
        !petService.checkout_date
      ) {
        return;
      }
      const payload = {
        client_id: clientId,
        pet_id: petService.pet_id,
        room_service_id: petService.service_id,
        room_type_id: petService.roomtype_id,
        checkin_date: petService.checkin_date,
        checkout_date: petService.checkout_date,
      };
      const key = JSON.stringify(payload);
      if (
        priceCalcLastParamsRef.current === key &&
        !priceCalcInFlightRef.current
      ) {
        // Already calculated for this exact set
        return;
      }
      priceCalcLastParamsRef.current = key;
      if (priceCalcTimerRef.current) clearTimeout(priceCalcTimerRef.current);
      priceCalcTimerRef.current = setTimeout(() => {
        calculatePriceFromAPI();
      }, 200);
    }, [
      clientId,
      petService.pet_id,
      petService.service_id,
      petService.roomtype_id,
      petService.checkin_date,
      petService.checkout_date,
    ]);

    const calculatePriceFromAPI = async () => {
      // Check if all required fields are available for API call
      if (
        (!clientId ||
          !petService.pet_id ||
          !petService.service_id ||
          !petService.roomtype_id ||
          !petService.checkin_date ||
          !petService.checkout_date) &&
        !isLoading
      ) {
        return;
      }

      // ✅ Prevent duplicate calculations for same parameters
      const currentParamsKey = `${petService.service_id}_${petService.roomtype_id}_${petService.checkin_date}_${petService.checkout_date}_${petService.pet_id}`;
      if (
        priceCalcLastParamsRef.current === currentParamsKey &&
        priceCalcInFlightRef.current
      ) {
        return;
      }

      try {
        const requestPayload = {
          client_id: clientId,
          pet_id: petService.pet_id,
          room_service_id: petService.service_id,
          room_type_id: petService.roomtype_id,
          checkin_date: petService.checkin_date,
          checkout_date: petService.checkout_date,
        };

        priceCalcInFlightRef.current = true;
        priceCalcLastParamsRef.current = currentParamsKey;

        const response = await httpService.post(
          "/vendorbooking/getPriceCalculation?service_type=boarding",
          requestPayload,
        );

        // Check if response exists and handle different response structures
        if (response && response.data !== undefined) {
          let calculatedPrice = 0;
          let gstAmount = 0;
          let taxPercentage = 0;
          let surchargeDetails = [];
          let salePrice = 0;
          let discountPercentage = 0;
          let discountAmount = 0;
          let appliedDiscount = null;
          let mrpPrice = 0;
          let discountedTax = 0;

          if (typeof response.data === "number") {
            // Direct number response
            calculatedPrice = response.data;
            salePrice = response.data;
            mrpPrice = response.data;
          } else if (
            typeof response.data === "object" &&
            response.data !== null
          ) {
            calculatedPrice = response.data.price || 0;
            gstAmount = response.data.taxAmount || 0;
            taxPercentage = response.data.taxPercentage || 0;
            surchargeDetails = response.data.surchargeDetails || [];
            salePrice = response.data.sale_price || calculatedPrice;
            discountPercentage = response.data.discount_percentage || 0;
            discountAmount = response.data.discount_amount || 0;
            appliedDiscount = response.data.applied_discount || null;
            mrpPrice = response.data.mrp_price || calculatedPrice;
            discountedTax = response.data.discounted_tax || 0;
          }

          const nights = petService.duration || 1;
          const perNightPrice =
            nights > 0 ? calculatedPrice / nights : calculatedPrice;
          const perNightMrp = nights > 0 ? mrpPrice / nights : mrpPrice;
          const perNightSalePrice = nights > 0 ? salePrice / nights : salePrice;

          setPetService((prev) => ({
            ...prev,
            service_type_name:
              response.data?.service_category?.service_type_name ||
              prev.service_type_name ||
              "",
            price: calculatedPrice,
            unit_price: perNightPrice,
            mrp_price: mrpPrice,
            sale_price: salePrice,
            discount_percentage: discountPercentage,
            discount_amount: discountAmount,
            applied_discount: appliedDiscount,
            price_surcharge: surchargeDetails,
            totalAmount: salePrice,
            tax_percentage: taxPercentage,
            gst_amount: gstAmount,
            discounted_tax: discountedTax,
            size_surcharge:
              surchargeDetails.find((s) => s.name === "size_surcharge")
                ?.price || 0,
            room_type_surcharge:
              surchargeDetails.find((s) => s.name === "room_type_surcharge")
                ?.price || 0,
            service_details: prev.service_details,
            calculated_discounts: {
              long_stay: 0,
              multiple_boarding: 0,
              client_label: 0,
            },
            price_calculated_from_api: true,
            last_calculated_params: currentParamsKey,
          }));

          // ✅ Set the ref to true when price is calculated
          priceCalculatedRef.current = true;
        } else {
          console.error("Invalid response structure:", response);
          toast.error(
            "Failed to calculate price: Invalid response from server",
          );
        }
      } catch (error) {
        console.error("Error calling price calculation API:", error);
        if (error.response) {
          toast.error(
            `Failed to calculate price: ${error.response.data?.message || "Server error"}`,
          );
        } else {
          toast.error(
            "Failed to calculate price. Please check your connection.",
          );
        }
      } finally {
        priceCalcInFlightRef.current = false;
      }
    };

    const fetchServiceDetails = async (serviceId) => {
      try {
        if (!serviceId) return;

        if (
          serviceFetchInFlightRef.current &&
          lastFetchedServiceIdRef.current === serviceId
        ) {
          return;
        }

        if (
          lastFetchedServiceIdRef.current === serviceId &&
          petService.service_details
        ) {
          return;
        }

        serviceFetchInFlightRef.current = true;
        lastFetchedServiceIdRef.current = serviceId;

        const petSize =
          petDetails?.size?.size_label?.toLowerCase() ||
          petService.pet_size?.toLowerCase() ||
          null;
        const roomTypeId = petService.roomtype_id || null;

        // ✅ Build URL with ALL parameters including dates
        let apiUrl = `/vendorroomservice/getRoomService/${serviceId}`;
        const params = [];
        if (petSize) params.push(`size=${encodeURIComponent(petSize)}`);
        if (roomTypeId)
          params.push(`roomTypeId=${encodeURIComponent(roomTypeId)}`);
        // ✅ ALWAYS send dates if available
        if (petService.checkin_date)
          params.push(
            `checkinDate=${encodeURIComponent(petService.checkin_date)}`,
          );
        if (petService.checkout_date)
          params.push(
            `checkoutDate=${encodeURIComponent(petService.checkout_date)}`,
          );
        if (params.length) apiUrl += `?${params.join("&")}`;

        const response = await httpService.get(apiUrl);
        const serviceData = response.data;

        if (!serviceData) {
          console.error("No service data found in response");
          return;
        }

        setServiceDetailData(serviceData);

        // ✅ Find the room option with updated pricing
        let selectedRoomOption = null;
        if (
          petService.roomtype_id &&
          serviceData.room_type_surcharges?.options
        ) {
          selectedRoomOption = serviceData.room_type_surcharges.options.find(
            (opt) => opt.room_type_id === petService.roomtype_id,
          );
        }

        // ✅ Update with new date-based pricing
        const hasPriceFromAPI =
          petService.sale_price > 0 && petService.price > 0;

        setPetService((prev) => ({
          ...prev,
          basic_price: serviceData.basic_price || 0,
          mrp_price:
            selectedRoomOption?.mrp_price ||
            serviceData.mrp_price ||
            serviceData.basic_price ||
            0,
          sale_price:
            selectedRoomOption?.sale_price ||
            serviceData.sale_price ||
            serviceData.basic_price ||
            0,
          discount_percentage:
            selectedRoomOption?.discount_percentage ||
            serviceData.discount_percentage ||
            0,
          size_surcharge:
            serviceData.calculated_surcharges?.size_surcharge || 0,
          room_type_surcharge: selectedRoomOption?.price || 0,
          service_name: serviceData?.name || "",
          service_category_name:
            serviceData?.service_category?.service_category_name || "",
          service_type_name:
            serviceData?.service_category?.service_type_name || "",
          service_details: serviceData,
          ...(prev.service_id !== serviceId ? { service_id: serviceId } : {}),
        }));

        // Handle addons
        if (serviceData?.addons && serviceData.addons.length > 0) {
          const mapped = {};
          serviceData.addons.forEach((addon) => {
            const addonId = addon.addon_id || addon.adon_id || addon._id;
            if (!addonId) return;
            mapped[addonId] = {
              selected: false,
              price: addon.value === "Free" ? 0 : parseFloat(addon.value),
              label: addon.name || "",
            };
          });

          const selectedIds = new Set([
            ...(editMode && itemData?.addons?.length
              ? (itemData.addons || []).map(
                  (a) => a.addon_id || a.adon_id || a._id,
                )
              : []),
            ...Array.from(preselectedAddonIdsRef.current || new Set()),
          ]);

          Object.keys(mapped).forEach((id) => {
            if (selectedIds.has(id)) {
              mapped[id].selected = true;
            }
          });
          setServicesd(mapped);
        } else {
          setServicesd({});
        }
      } catch (error) {
        console.error("Failed to fetch service details:", error);
        toast.error("Failed to fetch service details");
      } finally {
        serviceFetchInFlightRef.current = false;
      }
    };

    useEffect(() => {
      if (!petService.service_id) {
        setSurchargeRooms([]);
        setSurchargeRoomsLoaded(false);
        setFilteredRooms([]);
        setRoomAvailabilityMessage("Please select a service first");
        return;
      }

      if (!serviceDetailData) {
        setSurchargeRoomsLoaded(false);
        setRoomAvailabilityMessage("Loading service details...");
        return;
      }

      const petTypeId = petDetails?.pet_type?._id;
      if (!petTypeId) {
        setSurchargeRoomsLoaded(false);
        setRoomAvailabilityMessage("Loading pet details...");
        return;
      }

      if (
        serviceDetailData.room_type_surcharges?.enabled === "yes" &&
        serviceDetailData.room_type_surcharges?.options?.length > 0
      ) {
        const basicPrice = serviceDetailData.basic_price || 0;
        const sizeSurcharge =
          serviceDetailData.calculated_surcharges?.size_surcharge || 0;
        const serviceDiscount = serviceDetailData.discount_percentage || 0;

        const surchargeRoomTypes = serviceDetailData.room_type_surcharges.options
          .filter(
            (option) =>
              Array.isArray(option.pet_type) &&
              option.pet_type.includes(petTypeId),
          )
          .map((option) => {
            const roomSurcharge = option.price || 0;
            const calculatedMrp = basicPrice + sizeSurcharge + roomSurcharge;
            const discountPercent = option.discount_percentage || serviceDiscount;
            let calculatedSalePrice = calculatedMrp;
            if (discountPercent > 0) {
              calculatedSalePrice = parseFloat(
                (calculatedMrp - (calculatedMrp * discountPercent) / 100).toFixed(2),
              );
            }

            return {
              _id: option.room_type_id,
              room_name: option.room_name,
              name: option.room_name,
              surcharge_price: roomSurcharge,
              mrp_price: option.mrp_price || calculatedMrp,
              sale_price: option.sale_price || calculatedSalePrice,
              discount_percentage: discountPercent,
              discount: option.discount,
              calculated_mrp: calculatedMrp,
              calculated_sale_price: calculatedSalePrice,
              pet_type: option.pet_type,
            };
          });

        setSurchargeRooms(surchargeRoomTypes);
        setSurchargeRoomsLoaded(true);
        setRoomAvailabilityMessage(
          surchargeRoomTypes.length > 0
            ? "Checking availability for rooms with surcharges..."
            : `No room types are configured for ${petDetails?.pet_type?.name || "this pet type"} for this service`,
        );
      } else if (serviceDetailData.room_type_surcharges?.enabled === "no") {
        setSurchargeRooms([]);
        setSurchargeRoomsLoaded(true);
        setFilteredRooms([]);
        setRoomAvailabilityMessage(
          "This service does not have room type surcharges configured",
        );
      }
    }, [serviceDetailData, petService.service_id, petDetails?.pet_type?._id]);
      // leaving a dangling selection with no matching option.
      useEffect(() => {
        if (!editDataFetched && !orderItemApplied) return;
        if (!petService.roomtype_id) return;
        //if (!petService.service_id || !serviceDetailData) return;
        if (!petService.service_id || !serviceDetailData) return;
        if (!petDetails?.pet_type?._id) return;
        if (!surchargeRoomsLoaded) return;

        const stillValid = surchargeRooms.some(
          (room) => room._id === petService.roomtype_id,
        );

        if (!stillValid) {
          toast.warn(
            "The previously selected room type isn't available for this pet/service. Please choose a room type again.",
          );
          priceCalculatedRef.current = false;
          setSelectedRoomType(null);
          setPetService((prev) => ({
            ...prev,
            roomtype_id: "",
            room: "",
            room_type_surcharge: 0,
            price_calculated_from_api: false,
            last_calculated_params: null,
          }));
        }
      }, [
        surchargeRooms,
        surchargeRoomsLoaded,
        petService.roomtype_id,
        petService.service_id,
        serviceDetailData,
        petDetails?.pet_type?._id,
        editDataFetched,
        orderItemApplied,
        editMode,
      ]);
    
    // Update form when pet details change (for edit mode and size-based pricing)
    useEffect(() => {
      if (petDetails && petDetails._id) {
        // Update the pet service state with current pet details
        setPetService((prev) => ({
          ...prev,
          // Update pet details when they change
          pet_name: petDetails.name,
          pet_type: petDetails.pet_type?.name,
          pet_size: petDetails.size?.size_label,
          pet_breed: petDetails.breed?.breed_name,
        }));

        // Recalculate price when pet details change
        if (
          petService.service_id &&
          petService.roomtype_id &&
          petService.checkin_date &&
          petService.checkout_date
        ) {
          schedulePriceCalculation();
        }
      }
    }, [petDetails, petService.service_details]);

    // Update price when service details are loaded in edit mode
    useEffect(() => {
      if (
        editMode &&
        petService.service_id &&
        petService.roomtype_id &&
        petService.checkin_date &&
        petService.checkout_date &&
        !petService.price
      ) {
        schedulePriceCalculation();
      }
    }, [
      editMode,
      petService.service_id,
      petService.service_details,
      petService.roomtype_id,
      petService.price,
    ]);

    // ✅ SINGLE SOURCE OF TRUTH - Only calculate price when ALL fields are selected
    useEffect(() => {
      // Check if all required fields are selected
      const allFieldsSelected =
        petService.pet_id &&
        petService.service_id &&
        petService.roomtype_id &&
        petService.checkin_date &&
        petService.checkout_date &&
        petDetails?.size?.size_label && // Pet size is loaded
        serviceDetailData; // Service details are loaded

      if (!allFieldsSelected) {
        return;
      }

      // Create a unique key for current selection
      const currentSelectionKey = `${petService.service_id}_${petService.roomtype_id}_${petService.checkin_date}_${petService.checkout_date}_${petService.pet_id}`;

      // Skip if already calculated for this exact selection
      if (
        petService.last_calculated_params === currentSelectionKey &&
        petService.price_calculated_from_api
      ) {
        return;
      }

      // Small delay to ensure all state updates are complete
      const timer = setTimeout(() => {
        schedulePriceCalculation();
      }, 300);

      return () => clearTimeout(timer);
    }, [
      petService.pet_id,
      petService.service_id,
      petService.roomtype_id,
      petService.checkin_date,
      petService.checkout_date,
      petDetails?.size?.size_label,
      serviceDetailData,
    ]);

    // Fetch service details when service is selected but details not loaded
    useEffect(() => {
      if (petService.service_id && !petService.service_details) {
        // Avoid extra fetches when coming from edit prefill and we already fetched
        if (
          lastFetchedServiceIdRef.current === petService.service_id &&
          serviceFetchInFlightRef.current
        ) {
          return;
        }
        // Do not auto-fetch if current service_id equals a known room or bed id length (24 hex) but not matching itemData.service_id
        if (
          itemData?.service_id &&
          petService.service_id !== itemData.service_id
        ) {
          return;
        }
        fetchServiceDetails(petService.service_id);
      }
    }, [
      petService.service_id,
      petService.service_details,
      itemData?.service_id,
    ]);

    // Ensure addons are displayed when service is selected (preserve addons from service)
    useEffect(() => {
      if (
        petService.service_details?.addons &&
        petService.service_details.addons.length > 0
      ) {
        const mapped = {};
        petService.service_details.addons.forEach((addon) => {
          const addonId = addon.addon_id || addon.adon_id || addon._id;
          if (!addonId) return;
          mapped[addonId] = {
            selected: false,
            price: addon.value === "Free" ? 0 : parseFloat(addon.value),
            label: addon.name || "",
          };
        });
        // Mark selected based on both itemData and order-based preselection
        const selectedIds = new Set([
          ...(editMode && itemData?.addons?.length
            ? (itemData.addons || []).map(
                (a) => a.addon_id || a.adon_id || a._id,
              )
            : []),
          ...Array.from(preselectedAddonIdsRef.current || new Set()),
        ]);
        Object.keys(mapped).forEach((id) => {
          if (selectedIds.has(id)) {
            mapped[id].selected = true;
          }
        });
        setServicesd(mapped);
      } else {
        // Don't clear addons here as they might be set from fetchServiceDetails
      }
    }, [petService.service_details]);

    // When editing from BookingDetailsDrawer, pull exact order item to preselect addons and room info
    // Replace the existing applyFromOrder useEffect with this version:

    useEffect(() => {
      // Add the same guard condition as your main init useEffect
      if (!isOpen || !editMode || !orderId || orderItemApplied) return;
      if (itemData?._isReschedule) {
        // ← ADD THIS LINE
        setOrderItemApplied(true);
        return;
      }
      // CRITICAL: Wait for roomTypes to load before proceeding
      if (roomTypes.length === 0) {
        return;
      }

      const applyFromOrder = async () => {
        try {
          const orderRes = await httpService.get(
            `/vendororder/getvendororder/${orderId}`,
          );
          const order = orderRes.data;
          const items = Array.isArray(order?.order_items)
            ? order.order_items
            : [];
          const target =
            items.find(
              (it) =>
                it.item_type?.toLowerCase?.() === "boarding" &&
                ((itemData?._id && it._id === itemData._id) ||
                  (itemData?.ref_id && it.ref_id === itemData.ref_id) ||
                  (itemData?.service_id &&
                    it.service_id === itemData.service_id &&
                    (itemData?.vendor_pet_id || itemData?.pet_id
                      ? it.vendor_pet_id ===
                        (itemData.vendor_pet_id || itemData.pet_id)
                      : true))),
            ) ||
            items.find((it) => it.item_type?.toLowerCase?.() === "boarding");

          if (!target) {
            console.warn(
              "⚠️ Could not find matching boarding item in order for preselection",
            );
            setOrderItemApplied(true);
            return;
          }

          // Validate room_type_id before using it
          const isValidRoomType = target.room_type_id
            ? roomTypes.some((room) => room._id === target.room_type_id)
            : false;

          if (target.room_type_id && !isValidRoomType) {
            console.warn(
              "⚠️ applyFromOrder - Invalid room_type_id in order:",
              target.room_type_id,
            );
            console.warn(
              "Available room types:",
              roomTypes.map((r) => ({
                id: r._id,
                name: r.room_name || r.name,
              })),
            );
          }

          // Preselect addon ids
          preselectedAddonIdsRef.current = new Set(
            (target.addons || []).map((a) => a.addon_id || a.adon_id || a._id),
          );

          // Preselect form ids
          if (target.form_ids && Array.isArray(target.form_ids)) {
            preselectedFormIdsRef.current = new Set(target.form_ids);
          }

          // Apply room type and room if valid
          preselectedRoomTypeIdRef.current = isValidRoomType
            ? target.room_type_id
            : null;
          preselectedRoomIdRef.current = isValidRoomType
            ? target.room_id
            : null;

          // Update form selections - only set valid room type
          setPetService((prev) => ({
            ...prev,
            service_id: target.service_id || prev.service_id,
            roomtype_id: isValidRoomType
              ? target.room_type_id
              : prev.roomtype_id,
            room: isValidRoomType ? target.room_id || prev.room : prev.room,
          }));

          // Ensure dependent data is fetched only for valid room types
          if (isValidRoomType && target.room_type_id) {
            await fetchRoomDetails(target.room_type_id);
          }
          if (target.service_id) {
            await fetchServiceDetails(target.service_id);
          }

          setOrderItemApplied(true);
        } catch (err) {
          console.error("Failed to prefill from order for edit mode:", err);
          setOrderItemApplied(true);
        }
      };

      applyFromOrder();
    }, [isOpen, editMode, orderId, itemData, orderItemApplied, roomTypes]); // Add roomTypes to dependency array

    // Fetch service details when room type is selected but no service is selected
    useEffect(() => {
      if (
        petService.roomtype_id &&
        !petService.service_id &&
        services &&
        services.length > 0
      ) {
        // Only auto-fetch when not in edit prefill (to avoid wrong service id fetches)
        if (itemData?._id || itemData?.service_id) return;
        const firstService = services[0];
        fetchServiceDetails(firstService._id);
      }
    }, [
      petService.roomtype_id,
      petService.service_id,
      services,
      itemData?._id,
      itemData?.service_id,
    ]);

    useEffect(() => {
      if (isLoading) return;
      const basePrice = parseFloat(petService.price || "0") || 0;
      const gstAmount = parseFloat(petService.gst_amount || "0") || 0;

      const addonsTotal = Object.values(servicesd || {}).reduce(
        (sum, addon) => {
          const addonPrice = parseFloat(addon.price);
          if (addon.selected && !isNaN(addonPrice)) {
            return sum + addonPrice;
          }
          return sum;
        },
        0,
      );
      const addonsTax = (addonsTotal * petService.tax_percentage) / 100;

      // Calculate subtotal including addons (for boarding, price is per day, not multiplied by duration)
      const subtotalRaw = basePrice + addonsTotal;
      const subtotal = Math.max(0, subtotalRaw);
      const total = Math.max(0, subtotal + addonsTax);

      setPetService((prev) => ({
        ...prev,
        totalAmount: isNaN(total) ? "0.00" : total.toFixed(2),
      }));
    }, [
      petService.price,
      petService.gst,
      petService.gst_amount,
      servicesd,
      petService.duration,
    ]);

    const resetFormData = () => {
      setPetDetails([]);
      setServicesd({}); // Reset addons
      setSelectedForms([]);
      preselectedFormIdsRef.current = new Set();
      setPetService({
        pet_id: "",
        size: "",
        breed: "",
        pet_type: "",
        appointment_date: "",
        appointment_start_time: "",
        service_id: "",
        roomtype_id: "",
        duration: 0,
        price: 0,
        totalAmount: 0,
        // Reset comprehensive pricing fields
        basic_price: 0,
        size_surcharge: 0,
        room_type_surcharge: 0,
        long_stay_discount: 0,
        client_label_discount: 0,
        calculated_discounts: {
          long_stay: 0,
          multiple_boarding: 0,
          client_label: 0,
        },
        notes: "",
        addons: [],
      });
      setHasReadOnlyDates(false);
    };
    
    const handleSaveAsEnquiry = async () => {
      await handleSaveOrders();
      setSaveEnquiryClicked?.(true);
    };

    const handleSaveOrders = async () => {
      try {
        // Validate required fields
        if (!petService?.pet_id) {
          toast.warn("Please select a pet", {
            position: "top-right",
          });
          return;
        }

        if (!petService?.service_id) {
          toast.warn("Please select a service", {
            position: "top-right",
          });
          return;
        }

        // Only block if an already-assigned booking lost its room.
        // if (editMode && itemData?.room_id && !petService?.room) {
        //   toast.warn(
        //     "No room selected. Please select any available room.",
        //     { position: "top-right" }
        //   );
        //   return;
        // }
        // Get pet, service, and room details for display
        const selectedPet = pets.find((pet) => pet._id === petService.pet_id);
        const selectedRoomType = roomTypes.find(
          (room) => room._id === petService.roomtype_id,
        );
        const selectedRoomNumber = rooms.find(
          (room) => room._id === petService.room,
        );

        // Calculate number of nights
        const nights = petService.duration || 1;

        // ✅ Calculate per-night price correctly
        const totalBasePrice = parseFloat(petService.price) || 0;
        const perNightPrice =
          nights > 0 ? totalBasePrice / nights : totalBasePrice;

        const selectedFormNames = selectedForms
          .map((form) => form.form_name || form.name || "")
          .filter(Boolean);
        const hasMandatoryForm = selectedForms.some(
          (form) => getFormRequirement(form) === "mandatory",
        );
        const selectedService = services.find(
          (service) => service._id === petService.service_id
        );

        // ✅ CLEAN BOARDING DATA: Backend will calculate all prices
        const boardingData = {
          item_type: "boarding",
          vendor_pet_id: petService.pet_id,
          service_id: petService.service_id,
          room_type_id: petService.roomtype_id,
          start_date: petService.checkin_date,
          end_date: petService.checkout_date,
          duration: petService.duration,
          quantity: petService.duration, // ✅ Set quantity to number of nights
          addons: itemData?.addons || [],
          price_surcharge: petService.price_surcharge || [],
          form_ids: selectedForms.map((form) => form._id),
          form_names: selectedFormNames,
          form_name: selectedFormNames[0] || "",
          form_has_mandatory: hasMandatoryForm,
          // Additional details for display
          pet_name: selectedPet?.name || "",
          pet_type:
            petDetails?.pet_type?.name || selectedPet?.pet_type?.name || "",
          pet_type_id: petDetails?.pet_type?._id || "",
          pet_size:
            petDetails?.size?.size_label || selectedPet?.size?.size_label || "",
          pet_breed:
            petDetails?.breed?.breed_name ||
            selectedPet?.breed?.breed_name ||
            "",
          room_type_name: selectedRoomType?.room_name || "",
          room_id: petService.room,
          room_number: selectedRoomNumber?.room_number || "",
          service_type_id: itemData?.service_type_id || serviceTypeId,
          service_type_name: itemData?.service_type_name || "Boarding",
          service_name: selectedService?.name || "",
          notes: petService?.notes || "",

          // ✅ Price fields - CRITICAL FIX
          price: totalBasePrice, // Total base price for all nights
          unit_price: perNightPrice, // ✅ Per-night price (for display)
          tax_percentage: parseFloat(petService.tax_percentage) || 0,
          gst: parseFloat(petService.gst_amount) || 0,
          discounted_tax: parseFloat(petService.discounted_tax) || 0,
          total_price: parseFloat(petService.totalAmount) || 0,

          // ✅ Calculate price per day (for display)
          price_per_day: perNightPrice,

          // Ensure ref_id is attached only when NOT originating from an enquiry
          ...(!itemData?._fromEnquiry &&
            itemData?.ref_id && { ref_id: itemData.ref_id }),
          ...(!itemData?._fromEnquiry && !itemData?.ref_id && itemData?._id
            ? { ref_id: itemData._id }
            : {}),
          ...(!itemData?._fromEnquiry &&
          !itemData?.ref_id &&
          !itemData?._id &&
          itemData?.id
            ? { ref_id: itemData.id }
            : {}),
        };

        // If we are editing an existing locally-added boarding in AddAppointment (calendar flow)
        // carry through the index markers so the parent can update its local list.
        if (itemData?._editIndex !== undefined) {
          boardingData._editIndex = itemData._editIndex;
          if (itemData?._originalBoarding) {
            boardingData._originalBoarding = itemData._originalBoarding;
          }
        }
        // ✅ CLEAN API PAYLOAD: Backend will calculate all prices and totals
        const apiPayload = {
          order_type: "offline",
          vendor_client_id: clientId,
          order_items: [
            {
              ...boardingData,
              // Send empty status for boarding queue updates, otherwise use ref status
              status: editMode
                ? itemData?._id
                  ? itemData?.status
                  : ""
                : appointmentStatusRef?.current || "",
            },
          ],
          ...(orderId && { order_id: orderId }),
          ...(itemData?.order_id && { order_id: itemData.order_id }),
        };

        try {
          // LOCAL FLOW (calendar create or history reorder) --------------------
          if (fromCalendar || fromHistoryReorder) {
            // Only update parent/local list; do not persist now
            if (onUpdate) onUpdate(boardingData);

            // Do not show global success toast here; parent handles UX
          } else {
            // ORDERS PAGE FLOW ----------------------------------------------
            // Check if this is being called from BookingDetailsDrawer vs Orders.js
            // BookingDetailsDrawer passes order_id in itemData or orderId prop
            // Orders.js doesn't pass order_id in itemData

            const isFromBookingDetailsDrawer = Boolean(itemData?.order_id);

            if (isFromBookingDetailsDrawer) {
              // We are inside BookingDetailsDrawer (edit/create) — persist immediately
              try {
                const response = await httpService.post(
                  "/vendororder/addVendorOrderOrUpdate",
                  apiPayload,
                );
                if (onUpdate) onUpdate(response.data);

                // Show success message for BookingDetailsDrawer flow
                if (editMode) {
                  toast.success("Boarding updated successfully!", {
                    position: "top-center",
                  });
                } else {
                  toast.success("Boarding added successfully!", {
                    position: "top-center",
                  });
                }

                // Refresh boarding calendar and queue data after successful save
                await refreshBoardingData();
              } catch (error) {
                console.error(
                  "Error saving boarding from BookingDetailsDrawer:",
                  error,
                );
                toast.error("Failed to save boarding");
                return;
              }
            } else {
              // We are inside Orders.js — hand data back to parent so it can decide when to persist
              if (onUpdate) onUpdate(boardingData);

              // Refresh boarding calendar and queue data after successful save
              await refreshBoardingData();
            }
          }
        } catch (error) {
          console.error("Error saving boarding:", error);
          toast.error("Failed to save boarding");
          return; // do not close drawer if failed
        }

        // Reset form and close drawer
        resetFormData();
        onClose();

        // Refresh calendar and queue data after successful save
        if (
          typeof window !== "undefined" &&
          typeof window.refreshBoardingData === "function"
        ) {
          window.refreshBoardingData();
        }
      } catch (error) {
        console.error("Error adding boarding:", error);
        toast.error("Failed to add boarding. Please try again", {
          position: "top-right",
        });
      }
    };

    // Function to refresh boarding calendar and queue data
    const refreshBoardingData = async () => {
      try {
        // Call the boarding calendar and queue APIs to refresh data
        if (
          typeof window !== "undefined" &&
          typeof window.refreshBookingData === "function"
        ) {
          // Use the global refresh function if available (from BookingCalendar)
          window.refreshBookingData();
        } else {
          // Fallback: Call APIs directly if global function is not available
          await Promise.all([
            httpService.post("/vendorbooking/getVendorBookings"),
            httpService.get("/vendorbooking/getVendorBookingQueues"),
          ]);
        }
      } catch (error) {
        console.error(
          "❌ BoardingDrawer - Error refreshing boarding data:",
          error,
        );
      }
    };

    // Effect to refresh data when drawer closes (for non-calendar flows)
    useEffect(() => {
      if (!isOpen && !fromCalendar && !renderAsContent) {
        // Refresh boarding data when drawer closes (only for non-calendar flows)
        refreshBoardingData();
      }
    }, [isOpen, fromCalendar, renderAsContent]);

    // Main component content (stable JSX, not a new component each render)
    const mainContent = (
      <>
        {showNewPetForm ? (
          <AddNewPet
            isOpen={showNewPetForm}
            onClose={() => {
              if (petDetails._id)
                setPetService((prev) => ({
                  ...prev,
                  pet_id: petDetails._id,
                }));
              setShowNewPetForm(false);
            }}
            onSave={(addedPetData) => {
              setPetService((prev) => ({
                ...prev,
                pet_id: addedPetData._id,
              }));
              fetchPetDetails(addedPetData._id);
              fetchVendorClientPets();
            }}
            clientId={clientId}
            isFullWidth={true}
            shouldHideSaveAndAdd={true}
            petId={isEditPet ? petDetails._id : null}
            existingPets={pets || []}
          />
        ) : (
          <>
            <Box
              sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1000,
                  backgroundColor: "#fff",
                  px: 2,
                  py: 1.5,
                  borderBottom: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {!renderAsContent && (
                  <button
                    onClick={onClose}
                    className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  >
                    <FiArrowLeft className="w-5 h-5 text-gray-700" />
                  </button>
                )}
              </Box>
              <Box
                sx={{
                  overflowY: "auto",
                  overflowX: "hidden",
                  px: 2,
                  pb: 14,
                  maxHeight: "calc(100vh - 100px)",
                }}
              >
                {/* Boarding form content - Modern UI similar to NewServiceDrawer */}
                <Grid container spacing={2}>
                  {/* Pet Selection */}
                  {!itemData?.ref_id && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={0.5}
                        className="flex items-center gap-2"
                        sx={{ fontSize: "17px" }}
                      >
                        <PetsIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                        Pet <span style={{ color: "red" }}>*</span>
                      </Typography>

                      <Box display="flex" gap={1}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={petService.pet_id}
                          onChange={(e) => {
                            if (isPetDisabled) return;
                            const petId = e.target.value;
                            setPetService((prev) => ({
                              ...prev,
                              pet_id: petId,
                              petDetails: null,
                            }));
                            fetchPetDetails(petId);
                          }}
                          disabled={isPetDisabled}
                          placeholder={
                            isPetDisabled ? "Pet pre-selected" : "Select pet"
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PetsIcon
                                  sx={{
                                    color: isPetDisabled
                                      ? "#9ca3af"
                                      : "#6b7280",
                                    fontSize: 18,
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            mb: 1,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: isPetDisabled
                                ? "#f9fafb"
                                : "white",
                              border: "1px solid #e5e7eb",
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                borderColor: isPetDisabled
                                  ? "#e5e7eb"
                                  : "#3b82f6",
                                boxShadow: isPetDisabled
                                  ? "none"
                                  : "0 4px 12px rgba(59, 130, 246, 0.15)",
                              },
                              "&.Mui-focused": {
                                borderColor: isPetDisabled
                                  ? "#e5e7eb"
                                  : "#3b82f6",
                                boxShadow: isPetDisabled
                                  ? "none"
                                  : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                              },
                            },
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#374151",
                            },
                          }}
                        >
                          {isPetDisabled && petService.pet_id ? (
                            <MenuItem value={petService.pet_id}>
                              {(() => {
                                const selectedPet = pets.find(
                                  (pet) => pet._id === petService.pet_id,
                                );
                                if (selectedPet) {
                                  const name = selectedPet?.name ?? null;
                                  const breedName =
                                    selectedPet?.breed?.breed_name ?? null;
                                  const age =
                                    selectedPet?.age &&
                                    selectedPet.age.toString().trim() !== ""
                                      ? `${selectedPet.age} yrs`
                                      : null;
                                  const sizeLabel =
                                    selectedPet?.size_details?.size_label ??
                                    selectedPet?.size?.size_label ??
                                    null;

                                  const leftChunk = [name, breedName]
                                    .filter(Boolean)
                                    .join(" - ");
                                  const pieces = [];
                                  if (leftChunk) {
                                    pieces.push(
                                      age ? `${leftChunk} (${age})` : leftChunk,
                                    );
                                  } else if (age) {
                                    pieces.push(age);
                                  }
                                  if (sizeLabel)
                                    pieces.push(
                                      sizeLabel.charAt(0).toUpperCase(),
                                    );
                                  return pieces.join(" | ");
                                }
                                return "Selected Pet";
                              })()}
                            </MenuItem>
                          ) : (
                            pets
                              .filter(
                                (pet) =>
                                  pet.status?.toLowerCase() !== "inactive",
                              )
                              ?.map((pet) => (
                                <MenuItem key={pet._id} value={pet._id}>
                                  {(() => {
                                    const name = pet?.name ?? null;
                                    const breedName =
                                      pet?.breed?.breed_name ?? null;
                                    const age =
                                      pet?.age &&
                                      pet.age.toString().trim() !== ""
                                        ? `${pet.age} yrs`
                                        : null;
                                    const sizeLabel =
                                      pet?.size_details?.size_label ??
                                      pet?.size?.size_label ??
                                      null;

                                    const leftChunk = [name, breedName]
                                      .filter(Boolean)
                                      .join(" - ");
                                    const pieces = [];
                                    if (leftChunk) {
                                      pieces.push(
                                        age
                                          ? `${leftChunk} (${age})`
                                          : leftChunk,
                                      );
                                    } else if (age) {
                                      pieces.push(age);
                                    }
                                    if (sizeLabel)
                                      pieces.push(
                                        sizeLabel.charAt(0).toUpperCase(),
                                      );
                                    return pieces.join(" | ");
                                  })()}
                                </MenuItem>
                              ))
                          )}
                        </TextField>
                        {!isPetDisabled && (
                          <>
                            {petDetails?._id && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setIsEditPet(true);
                                  setShowNewPetForm(true);
                                }}
                                sx={{
                                  minWidth: "60px",
                                  height: "40px",
                                  textTransform: "none",
                                  borderRadius: "12px",
                                  background:
                                    "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                                  border: "none",
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: "13px",
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.25)",
                                  "&:hover": {
                                    background:
                                      "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                                    boxShadow:
                                      "0 6px 16px rgba(99, 102, 241, 0.35)",
                                    transform: "translateY(-1px)",
                                    border: "none",
                                  },
                                  "& .MuiButton-startIcon": {
                                    marginRight: "6px",
                                    "& svg": {
                                      fontSize: "16px",
                                    },
                                  },
                                }}
                                style={{
                                  display: isPetDisabled
                                    ? "none"
                                    : "inline-flex",
                                }}
                              >
                                Edit
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setIsEditPet(false);
                                setShowNewPetForm(true);
                              }}
                              sx={{
                                minWidth: "100px",
                                height: "40px",
                                textTransform: "none",
                                borderRadius: "12px",
                                background:
                                  "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                                border: "none",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "13px",
                                boxShadow:
                                  "0 4px 12px rgba(99, 102, 241, 0.25)",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                                  boxShadow:
                                    "0 6px 16px rgba(99, 102, 241, 0.35)",
                                  transform: "translateY(-1px)",
                                  border: "none",
                                },
                                "& .MuiButton-startIcon": {
                                  marginRight: "6px",
                                  "& svg": {
                                    fontSize: "16px",
                                  },
                                },
                              }}
                              startIcon={<MdPets />}
                            >
                              Add Pet
                            </Button>
                          </>
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* Pet Details Display */}
                  {petDetails.length != 0 && (
                    <Grid item xs={12}>
                      <PetCard
                        pet={petDetails}
                        vaccinationLoading={petDetailsLoading}
                        onEdit={() => {
                          setIsEditPet(true);
                          setShowNewPetForm(true);
                        }}
                        serviceName={
                          serviceDetailData?.service_category
                            ?.service_category_name ||
                          petService.service_category_name ||
                          ""
                        }
                      />
                    </Grid>
                  )}

                  {/* Check-in and Check-out Dates */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                      sx={{ fontSize: "17px" }}
                    >
                      <FiCalendar className="w-4 h-4 text-blue-600" />
                      Check-in Date <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      type="date"
                      lang="en-IN"
                      fullWidth
                      size="small"
                      value={petService.checkin_date}
                      onChange={(e) =>
                        handlePetServiceChange("checkin_date", e.target.value)
                      }
                      inputProps={{
                        //min: new Date().toISOString().split("T")[0], // Cannot select past dates
                         min: minDateStr,
                        // max: petService.checkout_date || undefined,
                        readOnly: hasReadOnlyDates,
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: "#3b82f6",
                            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#3b82f6",
                            boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                          },
                        },
                      }}
                      placeholder="Select checkin date"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                      sx={{ fontSize: "17px" }}
                    >
                      <FiCalendar className="w-4 h-4 text-green-600" />
                      Check-out Date <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      type="date"
                      lang="en-IN"
                      fullWidth
                      size="small"
                      value={petService.checkout_date}
                      onChange={(e) =>
                        handlePetServiceChange("checkout_date", e.target.value)
                      }
                      inputProps={{
                        min:
                          petService.checkin_date || minDateStr,
                          //new Date().toISOString().split("T")[0], // Cannot be before check-in or today
                        readOnly: hasReadOnlyDates,
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: "#10b981",
                            boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#10b981",
                            boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)",
                          },
                        },
                      }}
                      placeholder="Select checkout date"
                    />
                  </Grid>

                  {/* Service Selection with MRP Price */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        className="flex items-center gap-2"
                        sx={{ fontSize: "17px" }}
                      >
                        <PetsIcon sx={{ fontSize: 16, color: "#10b981" }} />
                        Service <span style={{ color: "red" }}>*</span>
                      </Typography>
                      {serviceDetailData && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setShowServiceDetailsModal(true)}
                          sx={{
                            minWidth: "auto",
                            p: 0.5,
                            color: "#6b7280",
                            "&:hover": { color: "#3b82f6" },
                          }}
                        >
                          <FiEye size={18} />
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            View Details
                          </Typography>
                        </Button>
                      )}
                    </Box>

                    {/* Wrap the dropdown and the Add Service button in a flex row */}
                    <Box
                      display="flex"
                      gap={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={petService.service_id}
                        onChange={async (e) => {
                          const serviceId = e.target.value;

                          // Clear old service details immediately
                          setServiceDetailData(null);

                          // Reset pricing info in petService
                          setPetService((prev) => ({
                            ...prev,
                            service_id: serviceId,
                            basic_price: 0,
                            mrp_price: 0,
                            sale_price: 0,
                            discount_percentage: 0,
                            size_surcharge: 0,
                            room_type_surcharge: 0,
                            price_surcharge: [],
                            service_details: null,
                          }));

                          // Fetch new service details
                          if (serviceId) {
                            await fetchServiceDetails(serviceId);
                          }
                        }}
                        SelectProps={{
                          MenuProps: {
                            style: { zIndex: 99999 },
                            disablePortal: false,
                          },
                        }}
                        placeholder="Select service"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PetsIcon
                                sx={{ color: "#6b7280", fontSize: 18 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "white",
                            border: "1px solid #e5e7eb",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: "#10b981",
                              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                            },
                            "&.Mui-focused": {
                              borderColor: "#10b981",
                              boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)",
                            },
                          },
                        }}
                      >
                        {services && services.length > 0 ? (
                          services.map((service) => {
                            const mrpPrice =
                              Number(service.mrp_price) ||
                              Number(service.basic_price || 0);
                            const salePrice =
                              Number(service.sale_price) || mrpPrice;
                            const isFree = service.discount_percentage === 100;
                            const hasDiscount = salePrice < mrpPrice || isFree;

                            return (
                              <MenuItem key={service._id} value={service._id}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    alignItems: "center",
                                  }}
                                >
                                  <span>{service?.name}</span>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    {hasDiscount ? (
                                      <>
                                        <Typography
                                          component="span"
                                          sx={{
                                            textDecoration: "line-through",
                                            color: "#9ca3af",
                                            fontSize: "12px",
                                          }}
                                        >
                                          ₹{mrpPrice}
                                        </Typography>
                                        <Typography
                                          component="span"
                                          sx={{
                                            color: "#10b981",
                                            fontWeight: "bold",
                                            fontSize: "14px",
                                          }}
                                        >
                                          {isFree ? "FREE" : `₹${salePrice}`}
                                        </Typography>
                                        {service.discount_percentage > 0 && (
                                          <Chip
                                            label={`${service.discount_percentage}% OFF`}
                                            size="small"
                                            sx={{
                                              backgroundColor: isFree
                                                ? "#dcfce7"
                                                : "#fef3c7",
                                              color: isFree
                                                ? "#166534"
                                                : "#d97706",
                                              fontSize: "10px",
                                              height: "20px",
                                              "& .MuiChip-label": { px: 1 },
                                            }}
                                          />
                                        )}
                                      </>
                                    ) : (
                                      <Typography
                                        component="span"
                                        sx={{
                                          color: "#10b981",
                                          fontWeight: "bold",
                                          fontSize: "14px",
                                        }}
                                      >
                                        ₹{mrpPrice}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem value="" disabled>
                            No services available
                          </MenuItem>
                        )}
                      </TextField>

                      {/* ✅ ADD THIS BUTTON – opens RoomDrawer */}
                      {!isEmployee && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setShowRoomDrawer(true);
                        }}
                        startIcon={<AddIcon />}
                        sx={{
                          minWidth: "130px",
                          height: "40px",
                          textTransform: "none",
                          borderRadius: "12px",
                          background:
                            "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
                          color: "white",
                          fontWeight: 600,
                          fontSize: "13px",
                          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
                            boxShadow: "0 6px 16px rgba(99, 102, 241, 0.35)",
                            transform: "translateY(-1px)",
                          },
                          "& .MuiButton-startIcon": {
                            marginRight: "4px",
                            "& svg": { fontSize: "18px" },
                          },
                        }}
                      >
                        Add Service
                      </Button>
                      )}
                    </Box>
                  </Grid>

                  {/* Room Type Selection - ONLY rooms with surcharges from service */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                      sx={{ fontSize: "17px" }}
                    >
                      <MdLocalHotel sx={{ fontSize: 16, color: "#8b5cf6" }} />
                      Room Type <span style={{ color: "red" }}>*</span>
                      {petService.roomtype_id && (
                        <span
                          style={{
                            color: "#10b981",
                            fontWeight: "bold",
                            fontSize: "14px",
                            marginLeft: "8px",
                          }}
                        >
                          {petService.discount_percentage === 100
                            ? "FREE"
                            : `₹${(petService.price / (petService.duration || 1)).toFixed(2)}/day`}
                        </span>
                      )}
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={petService.roomtype_id || ""}
                      onChange={async (e) => {
                        const roomTypeId = e.target.value;

                        if (!petService.pet_id) {
                          toast.info(
                            "Please select a pet first to check room compatibility",
                          );
                          return;
                        }

                        const selectedRoomType = filteredRooms.find(
                          (room) => room._id === roomTypeId,
                        );

                        if (selectedRoomType?._id) {
                          setSelectedRoomType(selectedRoomType);

                          // ✅ Reset the price calculated ref when room type changes
                          priceCalculatedRef.current = false;

                          setPetService((prev) => ({
                            ...prev,
                            roomtype_id: selectedRoomType._id,
                            room: "",
                            room_type_surcharge:
                              selectedRoomType.surcharge_price || 0,
                            selected_room_mrp: selectedRoomType.calculated_mrp,
                            selected_room_sale_price:
                              selectedRoomType.calculated_sale_price,
                            selected_room_discount:
                              selectedRoomType.discount_percentage,
                            price_calculated_from_api: false,
                            last_calculated_params: null,
                          }));

                          //fetchRoomDetails(selectedRoomType._id);
                          currentRoomRef.current = "";
                          fetchRoomDetails(selectedRoomType._id, null, true);
                        } else {
                          setSelectedRoomType(null);
                          priceCalculatedRef.current = false;
                          setPetService((prev) => ({
                            ...prev,
                            roomtype_id: "",
                            room: "",
                            room_type_surcharge: 0,
                            price_calculated_from_api: false,
                            last_calculated_params: null,
                          }));
                        }
                      }}
                      SelectProps={{
                        MenuProps: {
                          style: { zIndex: 99999 },
                          disablePortal: false,
                        },
                      }}
                      placeholder={
                        petService.service_id
                          ? "Select room type"
                          : "Select service first"
                      }
                      disabled={
                        !petService.service_id || filteredRooms.length === 0
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdLocalHotel
                              sx={{ color: "#6b7280", fontSize: 18 }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: !petService.service_id
                            ? "#f9fafb"
                            : "white",
                          border: "1px solid #e5e7eb",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: !petService.service_id
                              ? "#e5e7eb"
                              : "#8b5cf6",
                            boxShadow: !petService.service_id
                              ? "none"
                              : "0 4px 12px rgba(139, 92, 246, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: !petService.service_id
                              ? "#e5e7eb"
                              : "#8b5cf6",
                            boxShadow: !petService.service_id
                              ? "none"
                              : "0 0 0 3px rgba(139, 92, 246, 0.1)",
                          },
                        },
                      }}
                    >
                      {!petService.service_id ? (
                        <MenuItem value="" disabled>
                          Please select a service first
                        </MenuItem>
                      ) : filteredRooms.length > 0 ? (
                        filteredRooms.map((roomType) => {
                          const roomSurcharge = roomType.surcharge_price || 0;
                          return (
                            <MenuItem key={roomType._id} value={roomType._id}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  width: "100%",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {roomType?.room_name || roomType?.name}
                                </Typography>
                                {roomSurcharge > 0 && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: "#6b7280",
                                      fontSize: "13px",
                                      ml: 1,
                                    }}
                                  >
                                    +₹{roomSurcharge}
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem value="" disabled>
                          {roomAvailabilityMessage ||
                            "No rooms with surcharges available"}
                        </MenuItem>
                      )}
                    </TextField>
                  </Grid>

                  {/* Room Number Selection */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                      sx={{ fontSize: "17px" }}
                    >
                      <MdLocalHotel sx={{ fontSize: 16, color: "#f59e0b" }} />
                      Room Number{" "}
                      <span className="italic text-sm font-light">
                        {!petService.pet_id
                          ? "Select pet first"
                          : roomAvailabilityMessage ||
                            (rooms.length === 0 ? "Loading rooms..." : "")}
                      </span>
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={petService.room || ""}
                      onChange={(e) => {
                        const selectedRoomNumber = roomsDetails.find(
                          (item) => item._id === e.target.value,
                        );
                        setPetService((prev) => ({
                          ...prev,
                          room: e.target.value,
                        }));
                      }}
                      SelectProps={{
                        MenuProps: {
                          style: { zIndex: 99999 },
                          disablePortal: false,
                        },
                      }}
                      placeholder={
                        !petService.pet_id
                          ? "Select pet first"
                          : rooms.length === 0
                            ? "Loading rooms..."
                            : "Select room number"
                      }
                      disabled={!petService.pet_id || rooms.length === 0}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MdLocalHotel
                              sx={{
                                color: !petService.pet_id
                                  ? "#9ca3af"
                                  : "#6b7280",
                                fontSize: 18,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: !petService.pet_id
                            ? "#f9fafb"
                            : "white",
                          border: "1px solid #e5e7eb",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: !petService.pet_id
                              ? "#e5e7eb"
                              : "#f59e0b",
                            boxShadow: !petService.pet_id
                              ? "none"
                              : "0 4px 12px rgba(245, 158, 11, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: !petService.pet_id
                              ? "#e5e7eb"
                              : "#f59e0b",
                            boxShadow: !petService.pet_id
                              ? "none"
                              : "0 0 0 3px rgba(245, 158, 11, 0.1)",
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {!petService.pet_id
                          ? "Please select a pet first"
                          : rooms.length === 0
                            ? "Loading rooms..."
                            : "Select room number"}
                      </MenuItem>
                      {(rooms || []).map((room) => (
                        <MenuItem key={room._id} value={room._id}>
                          {room.room_number}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Price Distribution Box - Only show when room type is selected */}
                  {petService.roomtype_id && serviceDetailData && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "16px",
                          background:
                            "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                          border: "1px solid #e2e8f0",
                          mb: 2,
                        }}
                      >
                        {/* Check if there are any surcharges or discounts */}
                        {(() => {
                          const hasSurcharge =
                            petService.room_type_surcharge > 0 ||
                            petService.size_surcharge > 0;
                          const hasDiscount =
                            petService.applied_discount ||
                            petService.discount_amount > 0;
                          const hasTax =
                            petService.tax_percentage > 0 &&
                            (petService.gst_amount > 0 ||
                              petService.discounted_tax > 0);

                          // If no surcharge and no discount, show everything in one line
                          if (!hasSurcharge && !hasDiscount) {
                            const totalAmount =
                              petService.sale_price || petService.price || 0;
                            const totalTax =
                              petService.discounted_tax ||
                              petService.gst_amount ||
                              0;

                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1.5,
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#475569" }}
                                >
                                  <strong>Duration:</strong>{" "}
                                  {petService.duration || 0}{" "}
                                  {petService.duration === 1
                                    ? "Night"
                                    : "Nights"}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ color: "#cbd5e1" }}
                                >
                                  |
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ color: "#475569" }}
                                >
                                  <strong>Service charge:</strong>{" "}
                                  {petService.tax_percentage || 0}%
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ color: "#cbd5e1" }}
                                >
                                  |
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ color: "#475569" }}
                                >
                                  <strong>Base Price:</strong> ₹
                                  {serviceDetailData?.basic_price ||
                                    petService.basic_price ||
                                    0}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ color: "#cbd5e1" }}
                                >
                                  |
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ color: "#475569", fontWeight: "bold" }}
                                >
                                  <strong>Amount:</strong> ₹
                                  {Number(totalAmount) % 1 === 0
                                    ? Number(totalAmount)
                                    : totalAmount.toFixed(2)}
                                  {hasTax &&
                                    totalTax > 0 &&
                                    ` (+ ₹${totalTax.toFixed(2)} includes taxes)`}
                                </Typography>
                              </Box>
                            );
                          }

                          // With surcharges or discounts - show multi-line format
                          return (
                            <>
                              {/* Line 1: Duration | Service charge */}
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1.5,
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  mb: 1,
                                  pb: 0.5,
                                  borderBottom: "1px dashed #cbd5e1",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#475569" }}
                                >
                                  <strong>Duration:</strong>{" "}
                                  {petService.duration || 0}{" "}
                                  {petService.duration === 1
                                    ? "Night"
                                    : "Nights"}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ color: "#cbd5e1" }}
                                >
                                  |
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ color: "#475569" }}
                                >
                                  <strong>Service charge:</strong>{" "}
                                  {petService.tax_percentage || 0}%
                                </Typography>
                              </Box>

                              {/* Line 2: Base Price | Surcharge | Discount | Amount */}
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1.5,
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                }}
                              >
                                {/* Base price */}
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#475569" }}
                                >
                                  <strong>Base Price:</strong> ₹
                                  {serviceDetailData?.basic_price ||
                                    petService.basic_price ||
                                    0}
                                </Typography>

                                {/* Surcharges - only show if there is actually a surcharge */}
                                {hasSurcharge && (
                                  <>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#cbd5e1" }}
                                    >
                                      |
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#475569" }}
                                    >
                                      <strong>Surcharge:</strong>{" "}
                                      {petService.room_type_surcharge > 0 && (
                                        <span>
                                          Room: ₹
                                          {petService.room_type_surcharge}
                                        </span>
                                      )}
                                      {petService.room_type_surcharge > 0 &&
                                        petService.size_surcharge > 0 &&
                                        " + "}
                                      {petService.size_surcharge > 0 && (
                                        <span>
                                          Size: ₹{petService.size_surcharge}
                                        </span>
                                      )}
                                    </Typography>
                                  </>
                                )}

                                {/* Discount - Only show if there is an actual discount */}
                                {hasDiscount && (
                                  <>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#cbd5e1" }}
                                    >
                                      |
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#475569" }}
                                    >
                                      <strong>Discount:</strong>{" "}
                                      {petService.applied_discount?.type ===
                                      "FLAT"
                                        ? `-₹${petService.discount_amount}`
                                        : `-${petService.discount_percentage}% (₹${petService.discount_amount})`}
                                    </Typography>
                                  </>
                                )}

                                {/* Always show Amount section */}
                                {petService.discount_percentage === 100 ? (
                                  <>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#cbd5e1" }}
                                    >
                                      |
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#10b981",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      <strong>Amount:</strong> FREE
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#cbd5e1" }}
                                    >
                                      |
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#475569",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      <strong>Amount:</strong> ₹
                                      {(() => {
                                        const totalAmount =
                                          petService.sale_price ||
                                          petService.price ||
                                          0;
                                        const totalTax =
                                          petService.discounted_tax ||
                                          petService.gst_amount ||
                                          0;
                                        const formattedAmount =
                                          Number(totalAmount) % 1 === 0
                                            ? Number(totalAmount)
                                            : totalAmount.toFixed(2);
                                        if (hasTax && totalTax > 0) {
                                          return `${formattedAmount} (+ ₹${totalTax.toFixed(2)} includes taxes)`;
                                        }
                                        return formattedAmount;
                                      })()}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            </>
                          );
                        })()}
                      </Box>
                    </Grid>
                  )}
                  {/* Form Section */}
                  <Grid item xs={12} sx={{ mt: 1, mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                      sx={{ fontSize: "17px" }}
                    >
                      <NotesIcon sx={{ fontSize: 16, color: "#14b8a6" }} />
                      Forms
                    </Typography>

                    <Autocomplete
                      multiple
                      openOnFocus
                      clearOnBlur={false}
                      // ✅ Only filter by search text — do NOT exclude selected items.
                      filterOptions={(options, state) => {
                        const input = String(state.inputValue || "")
                          .trim()
                          .toLowerCase();
                        if (!input) return options;
                        return options.filter((option) =>
                          String(option?.form_name || "")
                            .toLowerCase()
                            .includes(input),
                        );
                      }}
                      // ✅ Disable mandatory forms in the dropdown so they can't be deselected.
                      getOptionDisabled={(option) =>
                        getFormRequirement(option) === "mandatory"
                      }
                      loading={loadingForms}
                      options={availableForms}
                      value={selectedForms}
                      inputValue={formInputValue}
                      onInputChange={(event, newInputValue) =>
                        setFormInputValue(newInputValue)
                      }
                      onChange={(event, newValue) => {
                        // ✅ Mandatory forms are always locked; re‑inject them if needed.
                        const mandatoryForms = availableForms.filter(
                          (f) => getFormRequirement(f) === "mandatory",
                        );
                        const mandatoryIds = new Set(
                          mandatoryForms.map((f) => f._id),
                        );

                        const merged = [
                          ...mandatoryForms,
                          ...(newValue || []).filter(
                            (f) => !mandatoryIds.has(f._id),
                          ),
                        ];

                        // Deduplicate by _id
                        const unique = Array.from(
                          new Map(
                            merged
                              .filter(Boolean)
                              .map((item) => [item._id, item]),
                          ).values(),
                        );
                        setSelectedForms(unique);
                        setFormInputValue("");
                      }}
                      getOptionLabel={(option) => option.form_name || ""}
                      isOptionEqualToValue={(option, value) => {
                        if (option?._id && value?._id)
                          return option._id === value._id;
                        return (
                          String(option?.form_name || "").trim() ===
                          String(value?.form_name || "").trim()
                        );
                      }}
                      groupBy={(option) => {
                        if (option.category) {
                          return typeof option.category === "string"
                            ? option.category
                            : option.category.name || "No Category";
                        }
                        if (option.category_name) return option.category_name;
                        return "Select Forms";
                      }}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          const isMandatory =
                            getFormRequirement(option) === "mandatory";
                          const sent = isFormSent(option);

                          const chipSx = isMandatory
                            ? {
                                borderRadius: "16px",
                                backgroundColor: "#fee2e2",
                                border: "1px solid #fecaca",
                                height: "32px",
                                "& .MuiChip-label": {
                                  fontSize: "14px",
                                  color: "#b91c1c",
                                },
                              }
                            : {
                                borderRadius: "16px",
                                backgroundColor: "#ffedd5",
                                border: "1px solid #fed7aa",
                                height: "32px",
                                "& .MuiChip-label": {
                                  fontSize: "14px",
                                  color: "#b45309",
                                },
                              };

                          return (
                            <Chip
                              variant="outlined"
                              label={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                  }}
                                >
                                  <span>{option.form_name}</span>
                                  {sent && (
                                    <Box
                                      component="span"
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        px: 0.75,
                                        py: 0.15,
                                        borderRadius: "999px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: "#166534",
                                        backgroundColor: "#dcfce7",
                                        border: "1px solid #bbf7d0",
                                      }}
                                    >
                                      Sent
                                    </Box>
                                  )}
                                </Box>
                              }
                              key={option._id}
                              {...tagProps}
                              // ✅ Hide delete icon for mandatory forms
                              onDelete={
                                isMandatory ? undefined : tagProps.onDelete
                              }
                              sx={chipSx}
                            />
                          );
                        })
                      }
                      renderOption={(props, option) => {
                        const isMandatory =
                          getFormRequirement(option) === "mandatory";
                        const sent = isFormSent(option);
                        const badgeSx = isMandatory
                          ? {
                              backgroundColor: "#fee2e2",
                              color: "#b91c1c",
                              border: "1px solid #fecaca",
                            }
                          : {
                              backgroundColor: "#ffedd5",
                              color: "#b45309",
                              border: "1px solid #fed7aa",
                            };
                        return (
                          <li {...props} key={option._id || option.form_name}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                gap: 1.5,
                                py: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {option.form_name}
                                </Typography>
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    ...badgeSx,
                                  }}
                                >
                                  {isMandatory ? "Mandatory" : "Optional"}
                                </Box>
                              </Box>
                              {sent && (
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    color: "#166534",
                                    backgroundColor: "#dcfce7",
                                    border: "1px solid #bbf7d0",
                                  }}
                                >
                                  Sent
                                </Box>
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder={
                            selectedForms.length === 0
                              ? "Search and select forms..."
                              : ""
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: "white",
                              border: "1px solid #e5e7eb",
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                borderColor: "#10b981",
                                boxShadow:
                                  "0 4px 12px rgba(16, 185, 129, 0.15)",
                              },
                              "&.Mui-focused": {
                                borderColor: "#10b981",
                                boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)",
                              },
                            },
                          }}
                        />
                      )}
                    />

                    {/* Optional "Send" button – uncomment if needed */}
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                      }}
                    >
                      {/* 
    <Button
      variant="contained"
      size="small"
      color="success"
      startIcon={<FiSend />}
      disabled={sendingForms || selectedForms.length === 0}
      onClick={handleSendSelectedForms}
      sx={{
        textTransform: "none",
        borderRadius: "12px",
        boxShadow: 2,
        minWidth: 200,
        width: "fit-content",
      }}
    >
      {sendingForms ? "Sending..." : "Send Selected Form(s)"}
    </Button>
    */}
                    </Box>
                  </Grid>

                  {/* Service Notes - Collapsible */}
                  <Grid item xs={12}>
                    <Button
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                      sx={{ fontSize: "15px" }}
                      onClick={() => setShowServiceNotes(!showServiceNotes)}
                      startIcon={
                        showServiceNotes ? (
                          <CancelIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <AddIcon sx={{ fontSize: 16 }} />
                        )
                      }
                    >
                      {showServiceNotes ? "Cancel Notes" : "Add Service Notes"}
                    </Button>

                    {showServiceNotes && (
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        value={petService.notes}
                        onChange={(e) =>
                          handlePetServiceChange("notes", e.target.value)
                        }
                        placeholder="Add any special instructions or notes for this boarding..."
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "white",
                            border: "1px solid #e5e7eb",
                            "&:hover": {
                              borderColor: "#6b7280",
                              boxShadow: "0 4px 12px rgba(107, 114, 128, 0.15)",
                            },
                            "&.Mui-focused": {
                              borderColor: "#6b7280",
                              boxShadow: "0 0 0 3px rgba(107, 114, 128, 0.1)",
                            },
                          },
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: "#fff",
                  px: 2,
                  py: 1,
                  borderTop: "1px solid #eee",
                  boxShadow: "0 -2px 12px rgba(0,0,0,0.1)",
                  zIndex: 1000,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Button
                      variant="outlined"
                      fullWidth
                      color="secondary"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        resetFormData();
                        onClose();
                      }}
                      sx={{
                        py: 1.4,
                        fontWeight: "bold",
                        fontSize: "16px",
                        borderRadius: "12px",
                        textTransform: "none",
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {!editMode && (
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<SaveIcon />}
                        onClick={handleSaveAsEnquiry}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                          backgroundColor: "#7E5FF5",
                          "&:hover": { backgroundColor: "#6D4EE5" },
                        }}
                      >
                        Save Enquiry
                      </Button>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    {editMode && !fromHistoryReorder ? (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<SaveIcon />}
                        disabled={
                          roomAvailabilityMessage !== "" ||
                          !petDetails.size?.size_label
                        }
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        Update
                      </Button>
                    ) : fromCalendar || fromHistoryReorder ? (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={
                          roomAvailabilityMessage !== "" ||
                          !petDetails.size?.size_label
                        }
                        startIcon={<AddIcon />}
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        Add
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<SaveIcon />}
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        Save
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Service Details Modal */}
            {showServiceDetailsModal && serviceDetailData && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  zIndex: 2000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                }}
                onClick={() => setShowServiceDetailsModal(false)}
              >
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "20px",
                    maxWidth: 400,
                    width: "100%",
                    maxHeight: "80vh",
                    overflow: "auto",
                    p: 3,
                    position: "relative",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: "#1f2937" }}
                    >
                      {serviceDetailData?.name || "Service Details"}
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => setShowServiceDetailsModal(false)}
                      sx={{ minWidth: "auto", p: 0.5, color: "#6b7280" }}
                    >
                      ✕
                    </Button>
                  </Box>

                  {/* ✅ DISCOUNT INFO BOX - Show in modal */}
                  {serviceDetailData.applied_discount &&
                    serviceDetailData.discount_amount > 0 && (
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          background:
                            "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                          border: "1px solid #f59e0b",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <ReceiptIcon
                            sx={{ fontSize: 18, color: "#d97706" }}
                          />
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            sx={{ color: "#92400e" }}
                          >
                            Discount Applied
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "#78350f", mb: 0.5 }}
                        >
                          🎉{" "}
                          <strong>
                            {serviceDetailData.applied_discount?.name ||
                              "Discount"}
                          </strong>
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#78350f" }}>
                            Discount Amount:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: "#d97706" }}
                          >
                            -₹{serviceDetailData.discount_amount}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#78350f" }}>
                            Final Price per night:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: "#d97706" }}
                          >
                            ₹
                            {Math.round(
                              (serviceDetailData.sale_price || 0) /
                                (petService.duration || 1),
                            )}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#78350f" }}>
                            Total Final Price:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: "#d97706" }}
                          >
                            ₹{serviceDetailData.sale_price}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "#78350f", mt: 1, display: "block" }}
                        >
                          {serviceDetailData.applied_discount?.type === "FLAT"
                            ? `✨ You saved ₹${serviceDetailData.discount_amount} on this booking!`
                            : `✨ You saved ${serviceDetailData.discount_percentage}% (₹${serviceDetailData.discount_amount}) on this booking!`}
                        </Typography>
                      </Box>
                    )}

                  {/* Description */}
                  {serviceDetailData?.description && (
                    <>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ color: "#6b7280", mb: 0.5 }}
                      >
                        Description
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: "#374151" }}
                      >
                        {serviceDetailData.description}
                      </Typography>
                    </>
                  )}

                  {/* Inclusions */}
                  {serviceDetailData?.service_inclusions?.length > 0 && (
                    <>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ color: "#6b7280", mb: 0.5 }}
                      >
                        What's Included
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        {serviceDetailData.service_inclusions.map(
                          (inclusion, idx) => (
                            <Box key={idx} sx={{ mb: 1 }}>
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                sx={{ color: "#374151" }}
                              >
                                {inclusion.inclusion_headings}:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#6b7280", ml: 2 }}
                              >
                                {inclusion.selected_options?.join(", ") ||
                                  "None"}
                              </Typography>
                            </Box>
                          ),
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </>
    );

    return (
      <>
        {isLoading ? (
          <LoaderSpiner text="Loading . . ." />
        ) : renderAsContent ? (
          mainContent
        ) : (
          <Drawer
            anchor="right"
            open={isOpen}
            onClose={() => {
              resetFormData();
              onClose();
            }}
            ModalProps={{ style: { zIndex: 10000 } }}
            PaperProps={{
              sx: {
                width: isMobile ? "100%" : "70vw",
                maxWidth: 800,
                p: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflowX: "hidden",
              },
            }}
          >
            {mainContent}
          </Drawer>
        )}

        {/* ✅ Portal is now outside the conditional – always evaluated */}
        {showRoomDrawer && (
          <>
            {createPortal(
              <RoomDrawer
                isOpen={showRoomDrawer}
                onClose={() => setShowRoomDrawer(false)}
                onSaved={() => {
                  setShowRoomDrawer(false);
                  setServiceRefreshKey((currentKey) => currentKey + 1);
                }}
                serviceId={null}
              />,
              document.body,
            )}
          </>
        )}
      </>
    );
  },
);


export default BoardingDrawer;
